import React, { useState, useEffect, Suspense, lazy } from 'react';
import { fundamentalsDimensionGate } from '../../config/fundamentalsDimensionFlag';

// Fundamentals dimension (flag-gated): point-in-time SF1 metrics selectable as an
// EXPRESSION operand (source 'feature', leftFeature/rightFeature = 'fund.<metric>').
const FUNDAMENTAL_METRIC_OPTIONS = [
  { value: 'fund.pe', label: 'P/E ratio' },
  { value: 'fund.pb', label: 'P/B ratio' },
  { value: 'fund.ps', label: 'P/S ratio' },
  { value: 'fund.evebitda', label: 'EV/EBITDA' },
  { value: 'fund.roe', label: 'Return on equity (ROE)' },
  { value: 'fund.roa', label: 'Return on assets (ROA)' },
  { value: 'fund.roic', label: 'Return on invested capital (ROIC)' },
  { value: 'fund.netmargin', label: 'Net margin' },
  { value: 'fund.grossmargin', label: 'Gross margin' },
  { value: 'fund.de', label: 'Debt / equity' },
  { value: 'fund.currentratio', label: 'Current ratio' },
  { value: 'fund.divyield', label: 'Dividend yield' },
  { value: 'fund.revgrowth', label: 'Revenue growth (YoY)' },
];
import {
  X,
  AlertTriangle,
  Settings,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Copy,
  RotateCcw,
  Save,
  Database,
} from 'lucide-react';
import {
  Block,
  BlockType,
  StopType,
  QuantityType,
  CommissionType,
  ExitType,
  TakeProfitType,
  TimeFrame,
  AssetClass,
  ExpressionSourceType,
  TradingPlatform,
  getDefaultExpressionData,
  BlockData,
  ValidationResult,
} from '../../types';
import TooltipLabel from './TooltipLabel';
import { callEdgeFunction } from '../../utils/supabaseFunctions';
import { useStrategy } from '../../contexts/StrategyContext';
import { getOriginalSizing, sizingChangedFromOriginal, SIZING_SNAPSHOT_FIELDS } from '../../services/originalSizing';
import {
  computeBlockDependencies,
  formatDependencyRowFull,
  findShadowingRef,
  type DependencyRef,
} from './blockDependencies';
import {
  isPerBarSubtype,
  isPerTradeSubtype,
  subtypeLabel,
  perTradeContract,
  applyFnNameOverride,
  defaultCustomBlockData,
} from './customBlockSubtype';

// Monaco editor (~1 MB) only matters for the Custom block. Lazy-load so users
// who never open a Custom block don't pay the cost.
const CustomBlockEditor = lazy(() => import('./CustomBlockEditor'));
const CustomBlockEditorLoading = () => (
  <div className="p-6 text-center text-sm text-gray-500">
    Loading code editor…
  </div>
);

// Alert delivery config — used by every terminal block (Entry / Exit /
// StopLoss / TakeProfit). Renders webhook URL + HMAC secret + email recipient
// inputs plus a "Send test alert" button so users can verify their endpoint
// before a real signal fires.
const AlertDeliveryFields: React.FC<{
  blockData: BlockData;
  alertType: 'entry' | 'exit' | 'stop_loss' | 'take_profit';
  onChange: (field: string, value: string | number | boolean | string[]) => void;
}> = ({ blockData, alertType, onChange }) => {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testFailed, setTestFailed] = useState(false);

  const webhookUrl = (blockData.alertWebhookUrl as string | undefined) ?? '';
  const webhookSecret = (blockData.alertWebhookSecret as string | undefined) ?? '';
  const email = (blockData.alertEmail as string | undefined) ?? '';

  const sendTest = async () => {
    setTesting(true);
    setTestResult(null);
    setTestFailed(false);
    try {
      const r = await callEdgeFunction('alert-deliver', {
        alert: {
          id: 'test_' + Date.now(),
          type: alertType,
          blockId: 'test',
          symbol: 'TEST',
          direction: 'long',
          price: 100,
          quantity: 1,
          pnl: alertType === 'entry' ? undefined : 1.23,
          timestamp: new Date().toISOString(),
          message: `Test ${alertType.replace('_', ' ')} alert from Tradeatory`,
          strategy: 'Tradeatory test',
        },
        webhookUrl: webhookUrl || undefined,
        webhookSecret: webhookSecret || undefined,
        email: email || undefined,
      });
      const j = await r.json().catch(() => ({}));
      const parts: string[] = [];
      if (j.webhook && j.webhook !== 'no-url') {
        parts.push(`webhook: ${j.webhook}${j.webhookSigned ? ' (signed)' : ''}${j.webhookError ? ' — ' + j.webhookError : ''}`);
      }
      if (j.email && j.email !== 'no-recipient') {
        parts.push(`email: ${j.email}${j.emailError ? ' — ' + j.emailError : ''}`);
      }
      const allOk = (!j.webhookError) && (!j.emailError) && parts.length > 0;
      setTestFailed(!allOk);
      setTestResult(parts.length > 0 ? parts.join(' · ') : 'Nothing to send (fill webhook URL or email first)');
    } catch (e) {
      setTestFailed(true);
      setTestResult('Failed: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setTesting(false);
    }
  };

  const canTest = !!(webhookUrl || email);

  return (
    <div className="pl-4 ml-2 border-l-2 border-gray-200 space-y-2">
      <TextInput
        label="Webhook URL (TradingView-style POST)"
        tooltip="HTTP endpoint that receives a JSON payload on every triggered alert. Your server then handles broker execution — Tradeatory doesn't integrate with brokers."
        field="alertWebhookUrl"
        value={webhookUrl}
        onChange={onChange}
        placeholder="https://example.com/webhook"
      />
      <TextInput
        label="Webhook secret (optional)"
        tooltip="Shared secret used to sign each webhook with HMAC-SHA256. Tradeatory sends the signature in the X-Tradeatory-Signature header so your server can verify the alert is authentic."
        field="alertWebhookSecret"
        value={webhookSecret}
        onChange={onChange}
        placeholder="leave empty to skip signing"
      />
      <TextInput
        label="Email recipient"
        tooltip="Inbox to receive alert notifications (delivered via Resend)."
        field="alertEmail"
        value={email}
        onChange={onChange}
        placeholder="you@example.com"
      />
      <div className="flex items-center space-x-3 pt-1">
        <button
          type="button"
          onClick={sendTest}
          disabled={testing || !canTest}
          className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
            testing || !canTest
              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
        >
          {testing ? 'Sending…' : 'Send test alert'}
        </button>
        {testResult && (
          <span className={`text-xs ${testFailed ? 'text-red-600' : 'text-green-700'}`}>
            {testResult}
          </span>
        )}
      </div>
    </div>
  );
};

interface BlockEditorProps {
  selectedBlock: Block;
  onUpdate: (block: Block) => void;
  onClose: () => void;
  blocks: Block[];
  onDuplicate?: (block: Block) => void;
  onDelete?: (blockId: string) => void;
  validationResult?: ValidationResult;
  /** S8d (2026-06-03) — Dependencies panel navigation. When the user
   *  clicks a row in the Dependencies panel, the editor calls this with
   *  the referenced block's id. Parent is expected to select that
   *  block (e.g. setStrategyState({ selectedBlock })). Optional so the
   *  editor can be embedded in surfaces that don't support navigation
   *  (e.g. read-only preview modals) — rows degrade to static text. */
  onSelectBlockId?: (id: string) => void;
}

const INPUT_CLASS =
  'w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500';
const CHECKBOX_CLASS =
  'rounded border-gray-300 text-blue-600 focus:ring-blue-500';
const LABEL_CLASS = 'ml-2 text-sm text-gray-700';

const PRICE_SOURCES = [
  { value: 'open', label: 'Open' },
  { value: 'high', label: 'High' },
  { value: 'low', label: 'Low' },
  { value: 'close', label: 'Close' },
  { value: 'hl2', label: 'HL2' },
  { value: 'hlc3', label: 'HLC3' },
  { value: 'ohlc4', label: 'OHLC4' },
];

const BASIC_PRICE_SOURCES = PRICE_SOURCES.filter((source) =>
  ['close', 'hl2', 'hlc3', 'ohlc4'].includes(source.value)
);

interface FormFieldProps {
  label?: string;
  tooltip?: string;
  field: string;
  value: any;
  onChange: (field: string, value: any) => void;
  hasError?: boolean;
  hasWarning?: boolean;
}

const NumberInput: React.FC<
  FormFieldProps & {
    min?: number;
    max?: number;
    step?: number;
    parser?: (value: string) => number;
    // S8f (2026-06-03) — optional disabled state. Used by the Custom
    // Position Sizing section to grey out inputs that are shadowed
    // by an active runtime-ref override (e.g. qtyCalculatorBlockId).
    disabled?: boolean;
  }
> = ({
  label = 'Value',
  tooltip = 'Enter a numeric value',
  field,
  value,
  onChange,
  hasError,
  hasWarning,
  min,
  max,
  step = 1,
  parser = (v) => parseFloat(v),
  disabled = false,
}) => (
  <div>
    <div className="flex items-center justify-between">
      <TooltipLabel label={label} tooltip={tooltip} />
      {(hasError || hasWarning) && (
        <div className="flex items-center space-x-1">
          {hasError && <AlertTriangle className="w-4 h-4 text-red-500" />}
          {hasWarning && <AlertTriangle className="w-4 h-4 text-yellow-500" />}
        </div>
      )}
    </div>
    <input
      type="number"
      name={field}
      aria-label={label}
      value={value ?? min ?? 0}
      onChange={(e) => onChange(field, parser(e.target.value) || min || 0)}
      className={`${INPUT_CLASS} ${
        hasError
          ? 'border-red-300 bg-red-50'
          : hasWarning
          ? 'border-yellow-300 bg-yellow-50'
          : ''
      } ${disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
    />
  </div>
);

const SelectInput: React.FC<
  FormFieldProps & {
    options: Array<{ value: string; label: string }>;
    defaultValue?: string;
    // S8f (2026-06-03) — optional disabled state (matches NumberInput).
    disabled?: boolean;
  }
> = ({
  label = 'Select Option',
  tooltip = 'Choose an option',
  field,
  value,
  onChange,
  hasError,
  hasWarning,
  options,
  defaultValue,
  disabled = false,
}) => (
  <div>
    <div className="flex items-center justify-between">
      <TooltipLabel label={label} tooltip={tooltip} />
      {(hasError || hasWarning) && (
        <div className="flex items-center space-x-1">
          {hasError && <AlertTriangle className="w-4 h-4 text-red-500" />}
          {hasWarning && <AlertTriangle className="w-4 h-4 text-yellow-500" />}
        </div>
      )}
    </div>
    <select
      name={field}
      aria-label={label}
      value={value || defaultValue || options[0]?.value}
      onChange={(e) => onChange(field, e.target.value)}
      className={`${INPUT_CLASS} ${
        hasError
          ? 'border-red-300 bg-red-50'
          : hasWarning
          ? 'border-yellow-300 bg-yellow-50'
          : ''
      } ${disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
      disabled={disabled}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
);

const TextInput: React.FC<
  FormFieldProps & {
    placeholder?: string;
  }
> = ({
  label = 'Text Input',
  tooltip = 'Enter text value',
  field,
  value,
  onChange,
  hasError,
  hasWarning,
  placeholder,
}) => (
  <div>
    <div className="flex items-center justify-between">
      <TooltipLabel label={label} tooltip={tooltip} />
      {(hasError || hasWarning) && (
        <div className="flex items-center space-x-1">
          {hasError && <AlertTriangle className="w-4 h-4 text-red-500" />}
          {hasWarning && <AlertTriangle className="w-4 h-4 text-yellow-500" />}
        </div>
      )}
    </div>
    <input
      type="text"
      name={field}
      aria-label={label}
      value={value || ''}
      onChange={(e) => onChange(field, e.target.value)}
      className={`${INPUT_CLASS} ${
        hasError
          ? 'border-red-300 bg-red-50'
          : hasWarning
          ? 'border-yellow-300 bg-yellow-50'
          : ''
      }`}
      placeholder={placeholder}
    />
  </div>
);

const CheckboxInput: React.FC<
  FormFieldProps & {
    id?: string;
  }
> = ({
  label = 'Checkbox Option',
  tooltip = 'Toggle this option',
  field,
  value,
  onChange,
  id,
}) => {
  const inputId = id || field;
  return (
    <div className="flex items-center">
      <input
        type="checkbox"
        id={inputId}
        name={inputId}
        aria-label={label}
        checked={value || false}
        onChange={(e) => onChange(field, e.target.checked)}
        className={CHECKBOX_CLASS}
      />
      <div className={LABEL_CLASS}>
        <TooltipLabel label={label} tooltip={tooltip} />
      </div>
    </div>
  );
};

const SourceSelect: React.FC<
  FormFieldProps & {
    sourceOptions?: Array<{ value: string; label: string }>;
  }
> = ({
  label = 'Source',
  tooltip = 'Price data source',
  field = 'source',
  value,
  onChange,
  hasError,
  hasWarning,
  sourceOptions = BASIC_PRICE_SOURCES,
}) => (
  <SelectInput
    label={label}
    tooltip={tooltip}
    field={field}
    value={value}
    onChange={onChange}
    hasError={hasError}
    hasWarning={hasWarning}
    options={sourceOptions}
    defaultValue="close"
  />
);

const BlockEditor: React.FC<BlockEditorProps> = ({
  selectedBlock,
  onUpdate,
  onClose,
  blocks,
  onDuplicate,
  onDelete,
  validationResult,
  onSelectBlockId,
}) => {
  const [previewMode, setPreviewMode] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  // S8e (2026-06-03) — Custom-block code editor visibility. The
  // CustomBlockEditor is a full-screen modal that pre-S8e closed
  // the WHOLE BlockEditor when its X was clicked (because the
  // modal's onClose was wired to the parent's onClose, which
  // deselects the block). That made the new S8d Dependencies
  // panel — and every other field in the right-side panel —
  // unreachable for custom blocks. S8e splits the two: the modal
  // X now only hides the modal via local state, keeping the
  // block selected. The user re-opens via the "Open Code Editor"
  // button rendered by renderCustomBlockFields when the modal
  // is hidden, OR by re-selecting the block from the canvas.
  // Reset to OPEN whenever the selected block changes — preserves
  // the pre-S8e default of "auto-open when a custom block is
  // selected" so the new behavior only diverges on the close
  // event itself.
  const [isCodeEditorOpen, setIsCodeEditorOpen] = useState(true);
  useEffect(() => {
    setIsCodeEditorOpen(true);
  }, [selectedBlock.id]);
  const { loadedAt } = useStrategy();

  // "Restore original sizing" — recover this block's sizing as it was when the
  // strategy loaded (before any Backtest-panel or Builder edits overwrote it).
  // Only meaningful for sizing-bearing blocks (strategy_config / entry).
  const isSizingBlock = selectedBlock.type === BlockType.STRATEGY_CONFIG || selectedBlock.type === BlockType.ENTRY;
  const sizingDiffersFromOriginal = isSizingBlock &&
    sizingChangedFromOriginal(loadedAt ?? null, selectedBlock.id, (selectedBlock.data ?? {}) as Record<string, unknown>);
  const handleRestoreSizing = () => {
    const orig = getOriginalSizing(loadedAt ?? null, selectedBlock.id);
    if (!orig) return;
    const nd: Record<string, unknown> = { ...(selectedBlock.data ?? {}) };
    for (const k of SIZING_SNAPSHOT_FIELDS) {
      if (orig[k] !== undefined) nd[k] = orig[k]; else delete nd[k];
    }
    onUpdate({ ...selectedBlock, data: nd as BlockData });
  };

  // Ensure the block has data - if not, initialize it
  useEffect(() => {
    if (!selectedBlock.data) {
      const defaultData = getDefaultBlockData(selectedBlock.type);
      onUpdate({
        ...selectedBlock,
        data: defaultData,
        metadata: {
          version: selectedBlock.metadata?.version || '1.0.0',
          author: selectedBlock.metadata?.author || '',
          description: selectedBlock.metadata?.description || '',
          tags: selectedBlock.metadata?.tags || [],
          created: selectedBlock.metadata?.created || new Date(),
          modified: new Date(),
          locked: selectedBlock.metadata?.locked || false,
          collapsed: selectedBlock.metadata?.collapsed || false,
        },
      });
    }
  }, [selectedBlock.id, selectedBlock.data, selectedBlock.type, onUpdate]);

  // Fix: Add null check for blocks array
  // (Kept as a query landmark even if the local isn't currently read —
  // multiple branches below grow this dependency over time.)
  const _strategyConfig = blocks?.find(
    (b) => b.type === BlockType.STRATEGY_CONFIG
  )?.data;
  void _strategyConfig;

  const blockErrors =
    validationResult?.errors.filter((e) => e.blockId === selectedBlock.id) ||
    [];
  const blockWarnings =
    validationResult?.warnings.filter((w) => w.blockId === selectedBlock.id) ||
    [];

  useEffect(() => {
    setIsDirty(false);
  }, [selectedBlock.data?.indicator]);

  const handleInputChange = (field: string, value: string | number | boolean | string[]) => {
    setIsDirty(true);

    const updatedBlock = {
      ...selectedBlock,
      data: {
        ...(selectedBlock.data || {}),
        [field]: value,
      },
      metadata: {
        version: selectedBlock.metadata?.version || '1.0.0',
        author: selectedBlock.metadata?.author || '',
        description: selectedBlock.metadata?.description || '',
        tags: selectedBlock.metadata?.tags || [],
        created: selectedBlock.metadata?.created || new Date(),
        modified: new Date(),
        locked: selectedBlock.metadata?.locked || false,
        collapsed: selectedBlock.metadata?.collapsed || false,
      },
    };

    onUpdate(updatedBlock);
  };

  const handleDuplicate = () => {
    if (onDuplicate) {
      onDuplicate(selectedBlock);
    }
  };

  const handleDelete = () => {
    if (onDelete && confirm('Are you sure you want to delete this block?')) {
      onDelete(selectedBlock.id);
      onClose();
    }
  };

  const handleReset = () => {
    if (confirm('Reset all parameters to default values?')) {
      const defaultData = getDefaultBlockData(selectedBlock.type);
      onUpdate({
        ...selectedBlock,
        data: {
          ...defaultData,
          name: selectedBlock.data?.name || defaultData.name,
          description: selectedBlock.data?.description || defaultData.description,
        },
        metadata: {
          version: selectedBlock.metadata?.version || '1.0.0',
          author: selectedBlock.metadata?.author || '',
          description: selectedBlock.metadata?.description || '',
          tags: selectedBlock.metadata?.tags || [],
          created: selectedBlock.metadata?.created || new Date(),
          modified: new Date(),
          locked: selectedBlock.metadata?.locked || false,
          collapsed: selectedBlock.metadata?.collapsed || false,
        },
      });
    }
  };

  const getDefaultBlockData = (type: BlockType): BlockData => {
    switch (type) {
      case BlockType.STRATEGY_CONFIG:
        return {
          name: 'Strategy Configuration',
          title: 'My TSL Trading Strategy',
          shortTitle: '',
          overlay: true,
          pyramiding: 1,
          maxPositions: 1,
          default_qty_type: QuantityType.FIXED,
          default_qty_value: 1,
          initial_capital: 10000,
          // S4e (2026-06-02) — `currency: 'USD'` removed (dead display-only
          // field; engine doesn't price by currency). See audit doc.
          slippage: 1,
          commission_type: CommissionType.PERCENTAGE,
          // 2026-05 — lowered 0.1 → 0.06 to match the engine-side default
          // (see costModel.ts DEFAULT_COMMISSION_VALUE). Realistic crypto-
          // futures taker fee; existing strategies that already set their
          // own value are unaffected (this is the BUILDER seed for NEW
          // strategy_config blocks).
          commission_value: 0.06,
          calc_on_order_fills: true,
          calc_on_every_tick: false,
          process_orders_on_close: true,
          maxDrawdown: 20,
          // S4e (2026-06-02) — `maxRiskPerTrade: 2` removed (dead field;
          // engine reads `risk_per_trade` on entry blocks instead, and
          // `propFirm.maxRiskPerTradePct` for prop-firm caps — completely
          // separate fields). See docs/BLOCK_SETTINGS_AUDIT.md S4e section.
          precision: 2,
          platform: TradingPlatform.TRADINGVIEW,
          assetClass: AssetClass.FOREX,
          timeframe: TimeFrame.H1,
        };
      case BlockType.PRICE_DATA:
        return {
          name: 'Price Data',
          // S4f (2026-06-02): `symbol: 'EURUSD'` removed (dead UI field;
          // backtest uses the run-time selected symbol from BacktestPanel
          // + the optimizer's cross-symbol sweep, NOT a per-block override).
          // See docs/BLOCK_SETTINGS_AUDIT.md S4f section.
          timeframe: TimeFrame.H1,
          source: 'close',
          shift: 0,
          // S4a (2026-06-02) — `lookback` removed. The engine series is always
          // the full backtest range; the per-block "lookback" input never had
          // any effect. See docs/BLOCK_SETTINGS_AUDIT.md S4 section.
          description: 'Custom price data source for TSL strategy components',
        };
      // M14b — ENTRY/EXIT/STOP_LOSS/TAKE_PROFIT defaults moved later in this
      // switch. The pre-M14b versions previously sat here and shadowed the
      // M14b improvements because of switch fall-through semantics
      // (first-match-wins). Removed in M-A debt sweep; see lines ~700+ for
      // the canonical defaults that actually get used now.
      case BlockType.MOVING_AVERAGE:
        return {
          name: 'Moving Average',
          indicator: 'sma',
          period: 20,
          source: 'close',
          smoothing: 2,
          timeOffset: 0,
        };
      case BlockType.MOMENTUM_OSCILLATOR:
        return {
          name: 'Momentum/Oscillator',
          indicator: 'rsi',
          period: 14,
          overbought: 70,
          oversold: 30,
          source: 'close',
          fastPeriod: 12,
          slowPeriod: 26,
          signalPeriod: 9,
          kSmooth: 1,
          dPeriod: 3,
          period1: 7,
          period2: 14,
          period3: 28,
        };
      case BlockType.VOLATILITY:
        return {
          name: 'Volatility Indicator',
          indicator: 'atr',
          period: 14,
          smoothing: 2,
          multiplier: 2,
          stdDev: 2,
        };
      case BlockType.VOLUME:
        return {
          name: 'Volume Indicator',
          indicator: 'volume_sma',
          period: 20,
          session: 'daily',
          standardDeviations: 0,
        };
      case BlockType.TREND:
        return {
          name: 'Trend Indicator',
          indicator: 'adx',
          period: 14,
          threshold: 25,
          accelerationFactor: 0.02,
          maxAcceleration: 0.2,
          tenkanPeriod: 9,
          kijunPeriod: 26,
          senkouBPeriod: 52,
        };
      case BlockType.CUSTOM:
        // TSL migration: new custom blocks are safe-TSL from the first
        // keystroke (tslText + tslAst) — never host JS, which the engine
        // quarantines and would make the block a silent no-op.
        return {
          ...defaultCustomBlockData(),
          description: '',
          // S4d (2026-06-02): `outputTypes: ['value']` removed (dead field). See audit doc.
        };
      case BlockType.TIME_FILTER:
        // S8c (2026-06-03) — F5 fix: pre-S8c the Builder wrote
        // `sessionFilter: { sessions: ['european', 'american'] }`
        // (array of session keys) AND `sessions: {}` (empty
        // record). The engine's evalTimeFilter at
        // src/engine/graph.ts:196 reads ONLY `data.sessions` as a
        // boolean record (`{ asian?, european?, american? }`), so
        // the UI-visible European + American defaults were
        // invisible to the engine — a `filterType: 'session'`
        // block with the Builder default rejected every bar
        // (sessions: {} → no session enabled). S8c writes the
        // engine-canonical shape directly so the UI default
        // matches what the engine consumes. Back-compat for
        // saved strategies that still carry the legacy
        // `sessionFilter.sessions` array is handled inside
        // evalTimeFilter (normalizes array → record at read
        // time). See docs/BLOCK_SETTINGS_AUDIT.md S8c section +
        // s8c-f5-* conversion tests.
        return {
          name: 'Time Filter',
          filterType: 'session',
          sessions: { european: true, american: true },
          tradingDays: {},
          startTime: '09:00',
          endTime: '17:00',
          timezone: 'UTC',
        };
      case BlockType.LOGIC_GATE:
        return {
          name: 'Logic Gate',
          type: 'AND',
          invert: false,
          description: 'Combine multiple signals',
          // S4a (2026-06-02) — `threshold` and `fuzzyLogic` removed.
          // The engine's evalLogicGate (src/engine/graph.ts:532) only
          // dispatches on `type` (AND/OR/XOR/NAND/NOR) + `invert`. The
          // threshold + fuzzy-logic inputs the UI used to expose had
          // no engine code path. See docs/BLOCK_SETTINGS_AUDIT.md.
        };
      case BlockType.EXPRESSION:
        return {
          name: 'Expression',
          ...getDefaultExpressionData(),
        } as BlockData;
      case BlockType.CHART_PATTERNS:
        return {
          name: 'Chart Patterns',
          patternType: 'chart_pattern',
          // S6b (2026-06-02) — `trendline_break` removed from the default
          // selection because the engine's chartPatterns.ts does NOT
          // implement it (intentionally; CUSTOM blockType:'pattern' is
          // the canonical answer per the file header). Replaced with
          // `head_shoulders` which IS implemented.
          chartPatterns: ['support_resistance_break', 'head_shoulders'],
          sensitivity: 'medium',
          confirmationBars: 1,
          minPatternSize: 5,
        };
      case BlockType.CANDLESTICK_PATTERNS:
        return {
          name: 'Candlestick Patterns',
          patternType: 'candlestick',
          candlestickPatterns: ['doji', 'hammer'],
          sensitivity: 'medium',
          confirmationBars: 1,
        };
      // M-A — duplicate-case removed. A second `case BlockType.CUSTOM` lived
      // here with a pattern-detection code template; the earlier indicator-
      // style case at line ~590 wins by switch-order so this block was dead
      // code. Pattern-style custom blocks are still selectable via the editor
      // sub-type chooser. TECH DEBT: `BlockType.CUSTOM` covers two distinct
      // concepts (custom indicator vs custom pattern); next refactor should
      // split into `BlockType.CUSTOM_INDICATOR` and `BlockType.CUSTOM_PATTERN`.
      case BlockType.ENTRY:
        // S8c (2026-06-03) — F4 fix: the nested `stop_loss` and
        // `take_profit` literal objects that used to live here were
        // DEAD. The engine reads from the SEPARATE `STOP_LOSS` and
        // `TAKE_PROFIT` terminal block types (graphCompiler.ts
        // pulls plan.stop / plan.take from those, not from
        // entry.data.stop_loss). A grep of
        //   entry.data?.stop_loss / data?.take_profit / data.stop_loss.* /
        //   data.take_profit.*
        // across src/engine + src/components/Backtesting returns
        // zero matches. Seeding the nested literals here only
        // produced confusion (the dogfood report flagged this as
        // F4) — they suggested a coupling that doesn't exist. The
        // engine's stop/take wiring lives in the dedicated
        // STOP_LOSS / TAKE_PROFIT terminal blocks. Saved
        // strategies that still carry these nested objects are
        // harmless: the graphCompiler doesn't read them, so they
        // sit inert in saved JSON. New strategies no longer get
        // them seeded. See docs/BLOCK_SETTINGS_AUDIT.md S8c
        // section + s8c-f4-* conversion tests.
        return {
          name: 'TSL Entry Terminal Block',
          entryType: 'market',
          direction: 'long',
          logic: 'AND',
          alertOnEntry: false,
          useStrategyDefaults: true,
          quantity_type: QuantityType.FIXED,
          quantity_value: 1,
          risk_per_trade: 10,
          alertOnStopLoss: false,
          alertOnTakeProfit: false,
        };
      case BlockType.EXIT:
        return {
          name: 'TSL Exit Terminal Block',
          exitType: 'market',
          logic: 'AND',
          alertOnExit: false,
          exitMode: ExitType.FULL,
          partialQuantity: 50,
          alertOnStopLoss: false,
          alertOnTakeProfit: false,
        };
      case BlockType.STOP_LOSS:
        return {
          name: 'TSL Stop Loss Terminal Block',
          stopType: StopType.FIXED_POINTS,
          stopValue: 50,
          activationPrice: 0,
          limitOffset: 1,
          timeDuration: 60,
          timeUnit: 'minutes',
          volatilityMultiplier: 2,
          exitMode: ExitType.FULL,
          partialQuantity: 50,
          alertOnStopLoss: false,
        };
      case BlockType.TAKE_PROFIT:
        return {
          name: 'TSL Take Profit Terminal Block',
          tpType: TakeProfitType.FIXED_POINTS,
          tpValue: 100,
          exitMode: ExitType.FULL,
          partialQuantity: 50,
          alertOnTakeProfit: false,
        };
      default:
        return { name: 'TSL Block' };
    }
  };

  const getFieldValidation = (fieldName: string) => {
    const hasError = blockErrors.some((e) => e.field === fieldName);
    const hasWarning = blockWarnings.some((w) => w.field === fieldName);
    return { hasError, hasWarning };
  };

  // Enhanced TSL-compliant rendering functions
  const renderStrategyConfigFields = () => (
    <div className="space-y-4">
      <div className="bg-cyan-50 p-3 rounded-md border border-cyan-200">
        <h4 className="text-sm font-medium text-cyan-800 mb-2">
          TSL Strategy Foundation
        </h4>
        <p className="text-xs text-cyan-700 leading-relaxed">
          This block configures core strategy parameters and cannot be connected
          to other blocks. It influences strategy behavior through configuration
          inheritance.
        </p>
      </div>

      <TextInput
        label="Strategy Name"
        tooltip="Internal name for your trading strategy"
        field="name"
        value={selectedBlock.data.name}
        onChange={handleInputChange}
        placeholder="My Trading Strategy"
      />

      <TextInput
        label="Strategy Title"
        tooltip="Display title for your strategy"
        field="title"
        value={selectedBlock.data.title}
        onChange={handleInputChange}
        placeholder="My Trading Strategy"
        hasError={getFieldValidation('title').hasError}
        hasWarning={getFieldValidation('title').hasWarning}
      />

      <div className="grid grid-cols-2 gap-4">
        <TextInput
          label="Version"
          tooltip="Version number for tracking strategy updates"
          field="version"
          value={selectedBlock.data.version}
          onChange={handleInputChange}
          placeholder="1.0.0"
        />

        <SelectInput
          label="Asset Class"
          tooltip="Type of financial instruments to trade"
          field="assetClass"
          value={selectedBlock.data.assetClass}
          onChange={handleInputChange}
          options={Object.values(AssetClass).map((ac) => ({
            value: ac,
            label: ac.charAt(0).toUpperCase() + ac.slice(1),
          }))}
          defaultValue={AssetClass.FOREX}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <SelectInput
          label="Trading Platform"
          tooltip="Target trading platform for strategy deployment"
          field="platform"
          value={selectedBlock.data.platform}
          onChange={handleInputChange}
          options={Object.values(TradingPlatform).map((platform) => ({
            value: platform,
            label: platform.toUpperCase(),
          }))}
          defaultValue={TradingPlatform.TRADINGVIEW}
        />

        <SelectInput
          label="Timeframe"
          tooltip="Default chart timeframe for the strategy"
          field="timeframe"
          value={selectedBlock.data.timeframe}
          onChange={handleInputChange}
          options={Object.values(TimeFrame).map((tf) => ({
            value: tf,
            label: tf,
          }))}
          defaultValue={TimeFrame.H1}
        />
      </div>

      <NumberInput
        label="Initial Capital"
        tooltip="Starting capital for the strategy"
        field="initial_capital"
        value={selectedBlock.data.initial_capital}
        onChange={handleInputChange}
        hasError={getFieldValidation('initial_capital').hasError}
        hasWarning={getFieldValidation('initial_capital').hasWarning}
        min={100}
        step={100}
      />

      {/* S4e (2026-06-02): Currency SelectInput removed.
          The engine does not price by currency — the field was a pure UI
          decoration tied to the block summary's "Capital: 10000 USD"
          label. Removing the input + the summary suffix together. See
          docs/BLOCK_SETTINGS_AUDIT.md S4e section. */}

      <div className="grid grid-cols-2 gap-4">
        <SelectInput
          label="Default Quantity Type"
          tooltip="How position sizes are calculated by default"
          field="default_qty_type"
          value={selectedBlock.data.default_qty_type}
          onChange={handleInputChange}
          options={Object.values(QuantityType).map((type) => ({
            value: type,
            label: type.replace(/_/g, ' ').toUpperCase(),
          }))}
          defaultValue={QuantityType.FIXED}
        />

        <NumberInput
          label="Default Quantity Value"
          tooltip="Default quantity value"
          field="default_qty_value"
          value={selectedBlock.data.default_qty_value}
          onChange={handleInputChange}
          min={0.01}
          step={0.01}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <NumberInput
          label="Max Positions"
          tooltip="Maximum number of concurrent positions"
          field="maxPositions"
          value={selectedBlock.data.maxPositions}
          onChange={handleInputChange}
          min={1}
          parser={(v) => parseInt(v)}
        />

        <NumberInput
          label="Pyramiding"
          tooltip="Number of additional positions in same direction"
          field="pyramiding"
          value={selectedBlock.data.pyramiding}
          onChange={handleInputChange}
          min={1}
          parser={(v) => parseInt(v)}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <NumberInput
          label="Max Drawdown (%)"
          tooltip="Maximum acceptable drawdown percentage. Engine halts the run when crossed."
          field="maxDrawdown"
          value={selectedBlock.data.maxDrawdown}
          onChange={handleInputChange}
          min={1}
          max={100}
        />

        {/*
          S10d (2026-06-03) — F2 fix. mdl_buffer_pct opt-in MDL
          headroom cap (sizing.ts:356). When set + the engine
          supplies dailyLossFloor, riskCash is capped to
          (ctx.equity − dailyLossFloor) × mdl_buffer_pct/100.
          Blank/zero = cap disabled (matches engine default).
        */}
        <NumberInput
          label="MDL Buffer (%)"
          tooltip="Opt-in headroom cap on per-trade risk vs prop-firm MDL (max daily loss). 0 or blank = disabled. Typical: 95 for prop-firm accounts. Engine reads at sizing.ts:356."
          field="mdl_buffer_pct"
          value={(selectedBlock.data as Record<string, unknown>).mdl_buffer_pct as number | undefined}
          onChange={handleInputChange}
          min={0}
          max={100}
          step={1}
        />
      </div>

      {/*
        S10d — F1 fix. equity_basis selector (sizing.ts:339).
        'current_equity' (default) = compounding; 'initial_capital'
        = non-compounding fixed base. Completely different
        equity-curve shapes; not surfacing this was the biggest
        sizing-related UX gap.
      */}
      <div className="grid grid-cols-2 gap-4">
        <SelectInput
          label="Equity Basis"
          tooltip="Base for percent-equity / risk-based sizing. 'current_equity' (default) compounds with PnL; 'initial_capital' uses a fixed base (no compounding). Engine reads at sizing.ts:339."
          field="equity_basis"
          value={(selectedBlock.data as Record<string, unknown>).equity_basis as string | undefined}
          onChange={handleInputChange}
          options={[
            { value: 'current_equity', label: 'Current Equity (compounding)' },
            { value: 'initial_capital', label: 'Initial Capital (no compounding)' },
          ]}
          defaultValue="current_equity"
        />

        {/*
          S10d — F3 fix. tickSize override. atr_based stops on
          non-standard instruments need this; defaults to a
          symbol-table lookup otherwise.
        */}
        <NumberInput
          label="Tick Size"
          tooltip="Strategy-level tick-size override (in instrument units). Required for atr_based stops on non-standard instruments. Leave 0 to use the symbol's default."
          field="tickSize"
          value={(selectedBlock.data as Record<string, unknown>).tickSize as number | undefined}
          onChange={handleInputChange}
          min={0}
          step={0.0001}
        />
      </div>

      {/*
        S10d — F6 fix. max_leverage + max_notional_usd. Both are
        hard caps the engine applies on top of method-switch sizing
        (sizing.ts:419, 489-494). 0 / blank = off.
      */}
      <div className="grid grid-cols-2 gap-4">
        <NumberInput
          label="Max Leverage"
          tooltip="Dynamic notional cap: notional ≤ equity × max_leverage. 0 = off (unlimited). 1 = spot (no leverage). Engine reads at sizing.ts:419."
          field="max_leverage"
          value={(selectedBlock.data as Record<string, unknown>).max_leverage as number | undefined}
          onChange={handleInputChange}
          min={0}
          step={0.1}
        />
        <NumberInput
          label="Max Notional ($)"
          tooltip="Absolute notional cap: notional ≤ max_notional_usd. 0 = off. Engine reads at sizing.ts:489."
          field="max_notional_usd"
          value={(selectedBlock.data as Record<string, unknown>).max_notional_usd as number | undefined}
          onChange={handleInputChange}
          min={0}
          step={100}
        />
      </div>

      {/*
        S10d — F4 fix. Intraday + session lifecycle controls.
        Engine: resolveEffectiveConfig.ts:297-303 + the run loop in
        ClientSideBacktestEngine consume all six. Pre-S10d zero of
        these were exposed in the Builder; intraday-only authoring
        was unreachable without JSON.
      */}
      <div className="rounded border border-gray-200 p-3">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Intraday / Session controls</h4>
        <div className="space-y-2">
          <CheckboxInput
            label="Intraday only"
            tooltip="Reject entries outside the session window AND optionally flatten positions at session close."
            field="intradayOnly"
            value={(selectedBlock.data as Record<string, unknown>).intradayOnly as boolean | undefined}
            onChange={handleInputChange}
          />
          <CheckboxInput
            label="Flatten at session close"
            tooltip="When the session ends with an open position, close it. Requires intradayOnly = true."
            field="flattenAtSessionClose"
            value={(selectedBlock.data as Record<string, unknown>).flattenAtSessionClose as boolean | undefined}
            onChange={handleInputChange}
          />
          <CheckboxInput
            label="Halt for the rest of the day after session-close flatten"
            tooltip="Once a session-close flatten fires, refuse new entries for the remainder of the trading day."
            field="haltAfterSessionFlatten"
            value={(selectedBlock.data as Record<string, unknown>).haltAfterSessionFlatten as boolean | undefined}
            onChange={handleInputChange}
          />
          <NumberInput
            label="No new entries within (min) of close"
            tooltip="Refuse new entries this many minutes before the session ends. 0 = disabled."
            field="noNewEntriesAfterMinutesBeforeClose"
            value={(selectedBlock.data as Record<string, unknown>).noNewEntriesAfterMinutesBeforeClose as number | undefined}
            onChange={handleInputChange}
            min={0}
            step={1}
            parser={(v) => parseInt(v)}
          />
          <NumberInput
            label="Max hold (bars)"
            tooltip="Force-close any position after this many bars. 0 = no cap."
            field="maxHoldBars"
            value={(selectedBlock.data as Record<string, unknown>).maxHoldBars as number | undefined}
            onChange={handleInputChange}
            min={0}
            step={1}
            parser={(v) => parseInt(v)}
          />
          <NumberInput
            label="Cooldown after exit (bars)"
            tooltip="Refuse new entries for this many bars after a trade closes. 0 = no cooldown."
            field="cooldownBarsAfterExit"
            value={(selectedBlock.data as Record<string, unknown>).cooldownBarsAfterExit as number | undefined}
            onChange={handleInputChange}
            min={0}
            step={1}
            parser={(v) => parseInt(v)}
          />
        </div>
      </div>

      {/*
        S10d — F5 fix (lightweight). propFirm pass-through summary.
        Full propFirm editing remains in the Backtesting panel's
        PropFirmRulesEditor — wiring that component into the
        Builder is out of scope for S10d. We surface a small
        read-only summary + a placeholder textarea for users who
        want to drop propFirm JSON in directly.
      */}
      <details className="rounded border border-gray-200 p-3">
        <summary className="text-sm font-medium text-gray-700 cursor-pointer">Prop-firm preset (advanced)</summary>
        <div className="mt-2 text-xs text-gray-600 space-y-2">
          <div>
            The prop-firm rule engine (maxDailyLossPct, maxOverallLossPct,
            maxRiskPerTradePct, daily caps, MDL halts) is configured via the{' '}
            <span className="font-semibold">propFirm</span> object on this block.
            Full editing is in the Backtesting panel&apos;s Prop-Firm Rules editor;
            this textarea accepts JSON paste for advanced users.
          </div>
          <textarea
            className={`${INPUT_CLASS} font-mono text-xs`}
            rows={6}
            placeholder='{ "maxDailyLossPct": 5, "maxOverallLossPct": 10, ... }'
            value={(() => {
              const v = (selectedBlock.data as Record<string, unknown>).propFirm;
              return v != null ? JSON.stringify(v, null, 2) : '';
            })()}
            onChange={(e) => {
              const raw = e.target.value.trim();
              if (raw === '') {
                handleInputChange('propFirm', undefined as unknown as string);
                return;
              }
              try {
                const parsed = JSON.parse(raw);
                handleInputChange('propFirm', parsed as unknown as string);
              } catch {
                // Invalid JSON — user is mid-edit, don't clobber.
                // The textarea remains "dirty" until they fix it.
              }
            }}
          />
        </div>
      </details>

      <div className="grid grid-cols-2 gap-4">
        {/* Use a raw <input> here (not NumberInput) so blank reads as
            "rule disabled" instead of falling back to min=1 silently.
            The engine reads strategy_config.data.maxTradesPerDay; the
            gate only fires when the value is a positive finite number. */}
        <div>
          <div className="flex items-center justify-between">
            <TooltipLabel
              label="Max Trades / Day"
              tooltip="Strategy-level daily trade cap (Pine's oneTradePerDay / tradesToday counter). Set to 1 for one-trade-a-day rules. Enforced by the engine even with no prop-firm preset. Leave BLANK to disable — the engine treats blank as 'no cap'."
            />
          </div>
          <input
            type="number"
            name="maxTradesPerDay"
            aria-label="Max Trades / Day"
            value={
              typeof (selectedBlock.data as Record<string, unknown>).maxTradesPerDay === 'number'
                ? String((selectedBlock.data as Record<string, unknown>).maxTradesPerDay)
                : ''
            }
            placeholder="blank = no cap"
            onChange={(e) => {
              const raw = e.target.value.trim();
              if (raw === '') {
                handleInputChange('maxTradesPerDay', '' as unknown as string);
              } else {
                const n = parseInt(raw, 10);
                if (Number.isFinite(n) && n >= 1) handleInputChange('maxTradesPerDay', n);
              }
            }}
            min={1}
            step={1}
            className={INPUT_CLASS}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <NumberInput
          label="Slippage (points)"
          tooltip="Expected slippage in points for market orders"
          field="slippage"
          value={selectedBlock.data.slippage}
          onChange={handleInputChange}
          min={0}
          step={0.1}
        />

        <NumberInput
          label="Precision"
          tooltip="Decimal precision for calculations"
          field="precision"
          value={selectedBlock.data.precision}
          onChange={handleInputChange}
          min={0}
          max={8}
          parser={(v) => parseInt(v)}
        />
      </div>

      <SelectInput
        label="Commission Type"
        tooltip="How trading commissions are calculated"
        field="commission_type"
        value={selectedBlock.data.commission_type}
        onChange={handleInputChange}
        options={Object.values(CommissionType).map((type) => ({
          value: type,
          label: type.replace(/_/g, ' ').toUpperCase(),
        }))}
        defaultValue={CommissionType.PERCENTAGE}
      />

      <NumberInput
        label="Commission Value"
        tooltip="Commission amount (interpretation depends on commission type)"
        field="commission_value"
        value={selectedBlock.data.commission_value}
        onChange={handleInputChange}
        min={0}
        step={0.01}
      />

      <div className="space-y-3">
        <CheckboxInput
          label="Calculate on every tick"
          tooltip="Recalculate strategy on each price tick"
          field="calc_on_every_tick"
          value={selectedBlock.data.calc_on_every_tick}
          onChange={handleInputChange}
        />

        <CheckboxInput
          label="Calculate on order fills"
          tooltip="Recalculate strategy when orders are filled"
          field="calc_on_order_fills"
          value={selectedBlock.data.calc_on_order_fills !== false}
          onChange={handleInputChange}
        />

        <CheckboxInput
          label="Process orders on bar close"
          tooltip="Execute orders only at bar close"
          field="process_orders_on_close"
          value={selectedBlock.data.process_orders_on_close !== false}
          onChange={handleInputChange}
        />

        <CheckboxInput
          label="Overlay on main chart"
          tooltip="Display indicator on main price chart"
          field="overlay"
          value={selectedBlock.data.overlay !== false}
          onChange={handleInputChange}
        />
      </div>
    </div>
  );

  const renderPriceDataFields = () => (
    <div className="space-y-4">
      <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
        <h4 className="text-sm font-medium text-blue-800 mb-2 flex items-center">
          <Database className="w-4 h-4 mr-2" />
          TSL Price Data Override
        </h4>
        <p className="text-xs text-blue-700 leading-relaxed">
          This block overrides global price data for connected blocks. Only one
          price data block can connect to each destination. Use for
          multi-timeframe analysis or cross-asset strategies.
        </p>
      </div>

      {/* S4f (2026-06-02): Symbol TextInput removed from price_data block.
          The backtest engine uses the symbol selected at run-time (the
          BacktestPanel symbol picker / the optimizer's cross-symbol
          sweep), NOT a per-block override. The deployed runtime (Pine /
          Python templates) ALSO uses the deployment-time selected
          symbol, not the block field. This input was a UI promise the
          engine never honored. See docs/BLOCK_SETTINGS_AUDIT.md S4f. */}

      <SelectInput
        label="Timeframe"
        tooltip="Chart timeframe for this price data source"
        field="timeframe"
        value={selectedBlock.data.timeframe}
        onChange={handleInputChange}
        hasError={getFieldValidation('timeframe').hasError}
        hasWarning={getFieldValidation('timeframe').hasWarning}
        options={[
          { value: 'M1', label: '1 Minute' },
          { value: 'M5', label: '5 Minutes' },
          { value: 'M15', label: '15 Minutes' },
          { value: 'M30', label: '30 Minutes' },
          { value: 'H1', label: '1 Hour' },
          { value: 'H4', label: '4 Hours' },
          { value: 'D1', label: 'Daily' },
          { value: 'W1', label: 'Weekly' },
          { value: 'MN1', label: 'Monthly' },
        ]}
        defaultValue="H1"
      />

      <SourceSelect
        label="Default Price Source"
        tooltip="Default price value to use when this data source is referenced"
        field="source"
        value={selectedBlock.data.source}
        onChange={handleInputChange}
        hasError={getFieldValidation('source').hasError}
        hasWarning={getFieldValidation('source').hasWarning}
        sourceOptions={[
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'hl2', label: 'HL2 (High + Low) / 2' },
          { value: 'hlc3', label: 'HLC3 (High + Low + Close) / 3' },
          { value: 'ohlc4', label: 'OHLC4 (Open + High + Low + Close) / 4' },
        ]}
      />

      {/* S4a (2026-06-02): Lookback Period input removed.
          The engine always materializes the full backtest candle series;
          the per-block "lookback" input never had any effect at evaluation
          time. See docs/BLOCK_SETTINGS_AUDIT.md S4 section for the audit
          evidence and the dead-field cleanup rationale. */}

      <NumberInput
        label="Shift/Offset"
        tooltip="Shift the data forward (+) or backward (-) in time"
        field="shift"
        value={selectedBlock.data.shift ?? 0}
        onChange={handleInputChange}
        hasError={getFieldValidation('shift').hasError}
        hasWarning={getFieldValidation('shift').hasWarning}
        min={-100}
        max={100}
        parser={(v) => parseInt(v)}
      />
    </div>
  );

  // Complete Moving Average fields implementation
  const renderMovingAverageFields = () => (
    <div className="space-y-4">
      <div className="bg-indigo-50 p-3 rounded-md border border-indigo-200">
        <h4 className="text-sm font-medium text-indigo-800 mb-2">
          TSL Moving Average Indicator
        </h4>
        <p className="text-xs text-indigo-700 leading-relaxed">
          Moving average calculations. Must flow through Expression or Logic
          Gate blocks before reaching terminal blocks.
        </p>
      </div>

      <SelectInput
        label="Moving Average Type"
        tooltip="Type of moving average calculation"
        field="indicator"
        value={selectedBlock.data.indicator}
        onChange={handleInputChange}
        hasError={getFieldValidation('indicator').hasError}
        hasWarning={getFieldValidation('indicator').hasWarning}
        // S10k (2026-06-03) — F7 fix. MA dropdown now matches the
        // engine's movingAverage dispatcher exactly (src/engine/
        // indicators.ts:248-255). Added ALMA + DEMA; removed RMA
        // (the engine's RMA is a SMOOTHING fn used inside other
        // indicators, not a top-level MA selection — picking it
        // here previously routed through the default-case fall-
        // through and produced NaN).
        options={[
          { value: 'sma', label: 'Simple Moving Average (SMA)' },
          { value: 'ema', label: 'Exponential Moving Average (EMA)' },
          { value: 'wma', label: 'Weighted Moving Average (WMA)' },
          { value: 'hma', label: 'Hull Moving Average (HMA)' },
          { value: 'vwma', label: 'Volume Weighted Moving Average (VWMA)' },
          { value: 'dema', label: 'Double Exponential Moving Average (DEMA)' },
          { value: 'tema', label: 'Triple Exponential Moving Average (TEMA)' },
          { value: 'alma', label: 'Arnaud Legoux Moving Average (ALMA)' },
        ]}
        defaultValue="sma"
      />

      <NumberInput
        label="Period"
        tooltip="Number of periods for moving average calculation"
        field="period"
        value={selectedBlock.data.period}
        onChange={handleInputChange}
        hasError={getFieldValidation('period').hasError}
        hasWarning={getFieldValidation('period').hasWarning}
        min={1}
        max={500}
        parser={(v) => parseInt(v)}
      />

      <SourceSelect
        label="Price Source"
        tooltip="Which price data to use for calculation"
        field="source"
        value={selectedBlock.data.source}
        onChange={handleInputChange}
        hasError={getFieldValidation('source').hasError}
        hasWarning={getFieldValidation('source').hasWarning}
        sourceOptions={PRICE_SOURCES}
      />

      <NumberInput
        label="Time Offset"
        tooltip="Shift the moving average forward or backward in time"
        field="timeOffset"
        value={selectedBlock.data.timeOffset || 0}
        onChange={handleInputChange}
        min={-100}
        max={100}
        parser={(v) => parseInt(v)}
      />

      {(selectedBlock.data.indicator === 'ema' ||
        selectedBlock.data.indicator === 'tema') && (
        <NumberInput
          label="Smoothing Factor"
          tooltip="Smoothing factor for exponential moving averages (2 = standard)"
          field="smoothing"
          value={selectedBlock.data.smoothing || 2}
          onChange={handleInputChange}
          min={1}
          max={10}
          step={0.1}
        />
      )}
    </div>
  );

  // Complete Momentum/Oscillator fields implementation
  const renderMomentumOscillatorFields = () => (
    <div className="space-y-4">
      <div className="bg-purple-50 p-3 rounded-md border border-purple-200">
        <h4 className="text-sm font-medium text-purple-800 mb-2">
          TSL Momentum & Oscillator Indicators
        </h4>
        <p className="text-xs text-purple-700 leading-relaxed">
          Merged momentum and oscillator indicators. Must flow through
          Expression or Logic Gate blocks.
        </p>
      </div>

      <SelectInput
        label="Indicator Type"
        tooltip="Type of momentum or oscillator indicator"
        field="indicator"
        value={selectedBlock.data.indicator}
        onChange={handleInputChange}
        hasError={getFieldValidation('indicator').hasError}
        hasWarning={getFieldValidation('indicator').hasWarning}
        options={[
          { value: 'rsi', label: 'Relative Strength Index (RSI)' },
          {
            value: 'macd',
            label: 'MACD (Moving Average Convergence Divergence)',
          },
          { value: 'stoch', label: 'Stochastic Oscillator' },
          { value: 'cci', label: 'Commodity Channel Index (CCI)' },
          { value: 'mfi', label: 'Money Flow Index (MFI)' },
          { value: 'roc', label: 'Rate of Change (ROC)' },
          { value: 'williams_r', label: 'Williams %R' },
          { value: 'ultimateOsc', label: 'Ultimate Oscillator' },
          // S10k (2026-06-03) — F8 fix. UI value 'awesome_osc'
          // changed to 'ao' to match engine's momentumIndicator
          // dispatch (src/engine/indicators.ts:500). Pre-S10k
          // selecting "Awesome Oscillator" produced NaN.
          { value: 'ao', label: 'Awesome Oscillator' },
          { value: 'trix', label: 'TRIX' },
          { value: 'momentum', label: 'Momentum' },
          { value: 'stoch', label: 'Stochastic' },
          { value: 'macd', label: 'MACD' },
        ]}
        defaultValue="rsi"
      />

      <NumberInput
        label="Period"
        tooltip="Primary period for the indicator calculation"
        field="period"
        value={selectedBlock.data.period}
        onChange={handleInputChange}
        hasError={getFieldValidation('period').hasError}
        hasWarning={getFieldValidation('period').hasWarning}
        min={1}
        max={200}
        parser={(v) => parseInt(v)}
      />

      <SourceSelect
        label="Price Source"
        tooltip="Which price data to use for calculation"
        field="source"
        value={selectedBlock.data.source}
        onChange={handleInputChange}
        hasError={getFieldValidation('source').hasError}
        hasWarning={getFieldValidation('source').hasWarning}
        sourceOptions={BASIC_PRICE_SOURCES}
      />

      {/* RSI, CCI, MFI, Williams %R specific fields */}
      {['rsi', 'cci', 'mfi', 'williams_r'].includes(
        selectedBlock.data?.indicator || ''
      ) && (
        <div className="grid grid-cols-2 gap-4">
          <NumberInput
            label="Overbought Level"
            tooltip="Level above which market is considered overbought"
            field="overbought"
            value={
              selectedBlock.data.overbought ||
              (selectedBlock.data.indicator === 'williams_r' ? -20 : 70)
            }
            onChange={handleInputChange}
            min={selectedBlock.data.indicator === 'williams_r' ? -50 : 50}
            max={selectedBlock.data.indicator === 'williams_r' ? 0 : 95}
          />
          <NumberInput
            label="Oversold Level"
            tooltip="Level below which market is considered oversold"
            field="oversold"
            value={
              selectedBlock.data.oversold ||
              (selectedBlock.data.indicator === 'williams_r' ? -80 : 30)
            }
            onChange={handleInputChange}
            min={selectedBlock.data.indicator === 'williams_r' ? -100 : 5}
            max={selectedBlock.data.indicator === 'williams_r' ? -50 : 50}
          />
        </div>
      )}

      {/* S5a (2026-06-02) — opt-in OB/OS auto-fire gate. Gated to the
          same indicator set the OB/OS NumberInputs are gated to (RSI /
          CCI / MFI / Williams %R). When enabled, the indicator block's
          signals[] series fires whenever the value is outside the
          [oversold, overbought] band — letting users wire the block
          DIRECTLY into a LogicGate without an intermediate Expression.
          Off by default; existing strategies see no behavior change.
          See docs/BLOCK_SETTINGS_AUDIT.md S5a section. */}
      {['rsi', 'cci', 'mfi', 'williams_r'].includes(
        selectedBlock.data?.indicator || ''
      ) && (
        <CheckboxInput
          label="Auto-fire signals on overbought/oversold extremes"
          tooltip="When enabled, the block emits a signal at every bar where the indicator value is outside the [oversold, overbought] band. Useful when wiring the block directly into a LogicGate without an Expression block in between. Off by default (legacy behavior — the block emits values only, and you wire an Expression to compare them)."
          field="emitExtremeSignals"
          value={!!selectedBlock.data.emitExtremeSignals}
          onChange={handleInputChange}
        />
      )}

      {/* MACD specific fields */}
      {selectedBlock.data.indicator === 'macd' && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <NumberInput
              label="Fast Period"
              tooltip="Fast EMA period for MACD calculation"
              field="fastPeriod"
              value={selectedBlock.data.fastPeriod || 12}
              onChange={handleInputChange}
              min={1}
              max={50}
              parser={(v) => parseInt(v)}
            />
            <NumberInput
              label="Slow Period"
              tooltip="Slow EMA period for MACD calculation"
              field="slowPeriod"
              value={selectedBlock.data.slowPeriod || 26}
              onChange={handleInputChange}
              min={1}
              max={100}
              parser={(v) => parseInt(v)}
            />
            <NumberInput
              label="Signal Period"
              tooltip="Signal line EMA period"
              field="signalPeriod"
              value={selectedBlock.data.signalPeriod || 9}
              onChange={handleInputChange}
              min={1}
              max={50}
              parser={(v) => parseInt(v)}
            />
          </div>
        </div>
      )}

      {/* Stochastic specific fields */}
      {selectedBlock.data.indicator === 'stoch' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <NumberInput
              label="K Period"
              tooltip="Period for %K calculation"
              field="period"
              value={selectedBlock.data.period || 14}
              onChange={handleInputChange}
              min={1}
              max={100}
              parser={(v) => parseInt(v)}
            />
            <NumberInput
              label="D Period"
              tooltip="Period for %D smoothing"
              field="dPeriod"
              value={selectedBlock.data.dPeriod || 3}
              onChange={handleInputChange}
              min={1}
              max={20}
              parser={(v) => parseInt(v)}
            />
          </div>
          <NumberInput
            label="K Smoothing"
            tooltip="Smoothing factor for %K"
            field="kSmooth"
            value={selectedBlock.data.kSmooth || 1}
            onChange={handleInputChange}
            min={1}
            max={10}
            parser={(v) => parseInt(v)}
          />
        </div>
      )}

      {/* Ultimate Oscillator specific fields. Accept the legacy 'ultimate'
          and 'ultimate_osc' aliases too so old strategies still render the
          right editor (TSL v1.1.1 A.1: canonical name is 'ultimateOsc'). */}
      {(selectedBlock.data.indicator === 'ultimateOsc'
        || selectedBlock.data.indicator === 'ultimate_osc'
        || selectedBlock.data.indicator === 'ultimate') && (
        <div className="grid grid-cols-3 gap-4">
          <NumberInput
            label="Period 1"
            tooltip="Short period for Ultimate Oscillator"
            field="period1"
            value={selectedBlock.data.period1 || 7}
            onChange={handleInputChange}
            min={1}
            max={50}
            parser={(v) => parseInt(v)}
          />
          <NumberInput
            label="Period 2"
            tooltip="Medium period for Ultimate Oscillator"
            field="period2"
            value={selectedBlock.data.period2 || 14}
            onChange={handleInputChange}
            min={1}
            max={100}
            parser={(v) => parseInt(v)}
          />
          <NumberInput
            label="Period 3"
            tooltip="Long period for Ultimate Oscillator"
            field="period3"
            value={selectedBlock.data.period3 || 28}
            onChange={handleInputChange}
            min={1}
            max={200}
            parser={(v) => parseInt(v)}
          />
        </div>
      )}
    </div>
  );

  // Complete Volatility fields implementation
  const renderVolatilityFields = () => (
    <div className="space-y-4">
      <div className="bg-orange-50 p-3 rounded-md border border-orange-200">
        <h4 className="text-sm font-medium text-orange-800 mb-2">
          TSL Volatility Indicators
        </h4>
        <p className="text-xs text-orange-700 leading-relaxed">
          Volatility and range-based indicators. Must flow through Expression or
          Logic Gate blocks.
        </p>
      </div>

      <SelectInput
        label="Volatility Indicator"
        tooltip="Type of volatility indicator"
        field="indicator"
        value={selectedBlock.data.indicator}
        onChange={handleInputChange}
        hasError={getFieldValidation('indicator').hasError}
        hasWarning={getFieldValidation('indicator').hasWarning}
        options={[
          // S10k (2026-06-03) — F11 fix. Volatility dropdown now
          // matches engine's volatilityIndicator dispatcher
          // (src/engine/indicators.ts:604-615). Removed
          // `true_range` and `historical_vol` — neither has an
          // engine arm; picking them fell through to NaN. Added
          // `chaikinVol` which IS dispatched. Same pattern as
          // the S6b chart_patterns cleanup.
          { value: 'atr', label: 'Average True Range (ATR)' },
          { value: 'bbands', label: 'Bollinger Bands' },
          { value: 'keltner', label: 'Keltner Channels' },
          { value: 'donchian', label: 'Donchian Channels' },
          { value: 'chaikinVol', label: 'Chaikin Volatility' },
        ]}
        defaultValue="atr"
      />

      <NumberInput
        label="Period"
        tooltip="Period for volatility calculation"
        field="period"
        value={selectedBlock.data.period}
        onChange={handleInputChange}
        hasError={getFieldValidation('period').hasError}
        hasWarning={getFieldValidation('period').hasWarning}
        min={1}
        max={200}
        parser={(v) => parseInt(v)}
      />

      {/* Bollinger Bands and Keltner specific fields */}
      {['bbands', 'keltner'].includes(selectedBlock.data?.indicator || '') && (
        <div className="space-y-4">
          <NumberInput
            label="Standard Deviations / Multiplier"
            tooltip="Number of standard deviations for band calculation"
            field="stdDev"
            value={selectedBlock.data.stdDev || 2}
            onChange={handleInputChange}
            min={0.1}
            max={5}
            step={0.1}
          />

          {selectedBlock.data.indicator === 'keltner' && (
            <SelectInput
              label="Moving Average Type"
              tooltip="Type of moving average for center line"
              field="maType"
              value={selectedBlock.data.maType || 'ema'}
              onChange={handleInputChange}
              options={[
                { value: 'sma', label: 'Simple Moving Average' },
                { value: 'ema', label: 'Exponential Moving Average' },
                { value: 'wma', label: 'Weighted Moving Average' },
              ]}
            />
          )}
        </div>
      )}

      {/* S5c (2026-06-02) — band selector for banded indicators (Bollinger /
          Keltner / Donchian). Default 'middle' preserves the pre-S5c
          behaviour where the block's values[] returned only the centerline.
          Selecting 'upper' or 'lower' lets users wire close-vs-upper or
          close-vs-lower Expressions DIRECTLY off the volatility block —
          closing the A1 deferred work from the Bollinger/Donchian saga
          (A2 used CUSTOM blocks as a workaround). See
          docs/BLOCK_SETTINGS_AUDIT.md S5c section. */}
      {['bbands', 'keltner', 'donchian'].includes(selectedBlock.data?.indicator || '') && (
        <SelectInput
          label="Band"
          tooltip="Which band of the indicator to expose as the block's value series. 'Middle' is the default (centerline / SMA / midpoint — pre-S5c behavior). 'Upper' and 'Lower' expose the corresponding band directly so you can wire close-vs-upper / close-vs-lower Expressions off this block without needing a CUSTOM block."
          field="band"
          value={selectedBlock.data.band || 'middle'}
          onChange={handleInputChange}
          options={[
            { value: 'middle', label: 'Middle (default)' },
            { value: 'upper',  label: 'Upper band' },
            { value: 'lower',  label: 'Lower band' },
          ]}
          defaultValue="middle"
        />
      )}

      {/* ATR specific fields */}
      {selectedBlock.data.indicator === 'atr' && (
        <SelectInput
          label="Smoothing Method"
          tooltip="Method for smoothing ATR calculation"
          field="smoothing"
          value={selectedBlock.data.smoothing || 'rma'}
          onChange={handleInputChange}
          options={[
            { value: 'rma', label: "RMA (Wilder's Smoothing)" },
            { value: 'sma', label: 'Simple Moving Average' },
            { value: 'ema', label: 'Exponential Moving Average' },
          ]}
        />
      )}

      {/* General multiplier for non-band indicators */}
      {!['bbands', 'keltner'].includes(selectedBlock.data?.indicator || '') && (
        <NumberInput
          label="Multiplier"
          tooltip="Multiplier for the indicator value"
          field="multiplier"
          value={selectedBlock.data.multiplier || 1}
          onChange={handleInputChange}
          min={0.1}
          max={10}
          step={0.1}
        />
      )}
    </div>
  );

  // Complete Volume fields implementation
  const renderVolumeFields = () => (
    <div className="space-y-4">
      <div className="bg-emerald-50 p-3 rounded-md border border-emerald-200">
        <h4 className="text-sm font-medium text-emerald-800 mb-2">
          TSL Volume Indicators
        </h4>
        <p className="text-xs text-emerald-700 leading-relaxed">
          Volume-based indicators and analysis. Must flow through Expression or
          Logic Gate blocks.
        </p>
      </div>

      <SelectInput
        label="Volume Indicator"
        tooltip="Type of volume indicator"
        field="indicator"
        value={selectedBlock.data.indicator}
        onChange={handleInputChange}
        hasError={getFieldValidation('indicator').hasError}
        hasWarning={getFieldValidation('indicator').hasWarning}
        // S10k (2026-06-03) — F14 fix. Volume dropdown matches
        // engine's volumeIndicator dispatcher (indicators.ts:
        // 712-724). Removed `volume_oscillator` and `pvt` (no
        // engine arms). Added `mfi`, `vroc`, `eom` which DO
        // dispatch. ad_line kept (the engine matches both
        // 'ad_line' and the camelCase 'adLine' in its switch).
        options={[
          { value: 'volume_sma', label: 'Volume Simple Moving Average' },
          { value: 'vwap', label: 'Volume Weighted Average Price (VWAP)' },
          { value: 'obv', label: 'On Balance Volume (OBV)' },
          { value: 'ad_line', label: 'Accumulation/Distribution Line' },
          { value: 'cmf', label: 'Chaikin Money Flow (CMF)' },
          { value: 'mfi', label: 'Money Flow Index (MFI)' },
          { value: 'vroc', label: 'Volume Rate of Change (VROC)' },
          { value: 'eom', label: 'Ease of Movement (EOM)' },
        ]}
        defaultValue="volume_sma"
      />

      {/* Period for indicators that use it */}
      {!['obv', 'ad_line', 'pvt'].includes(selectedBlock.data?.indicator || '') && (
        <NumberInput
          label="Period"
          tooltip="Period for volume indicator calculation"
          field="period"
          value={selectedBlock.data.period || 20}
          onChange={handleInputChange}
          min={1}
          max={200}
          parser={(v) => parseInt(v)}
        />
      )}

      {/* VWAP specific fields */}
      {selectedBlock.data.indicator === 'vwap' && (
        <SelectInput
          label="Session Reset"
          tooltip="When to reset VWAP calculation"
          field="session"
          value={selectedBlock.data.session || 'daily'}
          onChange={handleInputChange}
          options={[
            { value: 'daily', label: 'Daily Reset' },
            { value: 'weekly', label: 'Weekly Reset' },
            { value: 'monthly', label: 'Monthly Reset' },
            { value: 'never', label: 'Never Reset' },
          ]}
        />
      )}

      {/* Volume Oscillator specific fields */}
      {selectedBlock.data.indicator === 'volume_oscillator' && (
        <div className="grid grid-cols-2 gap-4">
          <NumberInput
            label="Fast Period"
            tooltip="Fast volume moving average period"
            field="fastPeriod"
            value={selectedBlock.data.fastPeriod || 12}
            onChange={handleInputChange}
            min={1}
            max={100}
            parser={(v) => parseInt(v)}
          />
          <NumberInput
            label="Slow Period"
            tooltip="Slow volume moving average period"
            field="slowPeriod"
            value={selectedBlock.data.slowPeriod || 26}
            onChange={handleInputChange}
            min={1}
            max={200}
            parser={(v) => parseInt(v)}
          />
        </div>
      )}

      {/* CMF specific fields */}
      {selectedBlock.data.indicator === 'cmf' && (
        <NumberInput
          label="Period"
          tooltip="Period for Chaikin Money Flow calculation"
          field="period"
          value={selectedBlock.data.period || 20}
          onChange={handleInputChange}
          min={1}
          max={100}
          parser={(v) => parseInt(v)}
        />
      )}
    </div>
  );

  // Complete Trend fields implementation
  const renderTrendFields = () => (
    <div className="space-y-4">
      <div className="bg-teal-50 p-3 rounded-md border border-teal-200">
        <h4 className="text-sm font-medium text-teal-800 mb-2">
          TSL Trend Indicators
        </h4>
        <p className="text-xs text-teal-700 leading-relaxed">
          Trend direction and strength indicators. Must flow through Expression
          or Logic Gate blocks.
        </p>
      </div>

      <SelectInput
        label="Trend Indicator"
        tooltip="Type of trend indicator"
        field="indicator"
        value={selectedBlock.data.indicator}
        onChange={handleInputChange}
        hasError={getFieldValidation('indicator').hasError}
        hasWarning={getFieldValidation('indicator').hasWarning}
        // S10k (2026-06-03) — F16 fix. Trend dropdown matches
        // engine's trendIndicator dispatcher (indicators.ts:838).
        // Removed `supertrend` and `trend_strength` (no engine
        // arms — produced NaN). The remaining five all dispatch.
        options={[
          { value: 'adx', label: 'Average Directional Index (ADX)' },
          { value: 'dmi', label: 'Directional Movement Index (DMI)' },
          { value: 'ichimoku', label: 'Ichimoku Cloud' },
          { value: 'parabolic_sar', label: 'Parabolic SAR' },
          { value: 'aroon', label: 'Aroon Indicator' },
        ]}
        defaultValue="adx"
      />

      <NumberInput
        label="Period"
        tooltip="Period for trend indicator calculation"
        field="period"
        value={selectedBlock.data.period}
        onChange={handleInputChange}
        hasError={getFieldValidation('period').hasError}
        hasWarning={getFieldValidation('period').hasWarning}
        min={1}
        max={200}
        parser={(v) => parseInt(v)}
      />

      {/* ADX/DMI specific fields */}
      {['adx', 'dmi'].includes(selectedBlock.data?.indicator || '') && (
        <>
          <NumberInput
            label="Threshold"
            tooltip="Threshold level for trend strength (typically 25)"
            field="threshold"
            value={selectedBlock.data.threshold || 25}
            onChange={handleInputChange}
            min={10}
            max={50}
          />
          {/* S5b (2026-06-02) — opt-in threshold auto-fire gate. When
              enabled, the trend block emits a signal at every bar
              where the indicator value EXCEEDS the threshold (the
              canonical "trending market" filter). Off by default;
              existing strategies see no behavior change. See
              docs/BLOCK_SETTINGS_AUDIT.md S5b section. */}
          <CheckboxInput
            label="Auto-fire signals when value exceeds threshold"
            tooltip="When enabled, the block emits a signal at every bar where the indicator value is strictly greater than the threshold (e.g. ADX > 25 ⇒ trending market). Useful when wiring the block directly into a LogicGate without an Expression block in between. Off by default (legacy behavior — the block emits values only, and you wire an Expression to compare them)."
            field="emitThresholdSignals"
            value={!!selectedBlock.data.emitThresholdSignals}
            onChange={handleInputChange}
          />
        </>
      )}

      {/* Parabolic SAR specific fields */}
      {selectedBlock.data.indicator === 'parabolic_sar' && (
        <div className="grid grid-cols-2 gap-4">
          <NumberInput
            label="Acceleration Factor"
            tooltip="Initial acceleration factor (typically 0.02)"
            field="accelerationFactor"
            value={selectedBlock.data.accelerationFactor || 0.02}
            onChange={handleInputChange}
            min={0.01}
            max={0.1}
            step={0.01}
          />
          <NumberInput
            label="Max Acceleration"
            tooltip="Maximum acceleration factor (typically 0.2)"
            field="maxAcceleration"
            value={selectedBlock.data.maxAcceleration || 0.2}
            onChange={handleInputChange}
            min={0.1}
            max={1}
            step={0.1}
          />
        </div>
      )}

      {/* Ichimoku specific fields */}
      {selectedBlock.data.indicator === 'ichimoku' && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <NumberInput
              label="Tenkan Period"
              tooltip="Conversion line period (typically 9)"
              field="tenkanPeriod"
              value={selectedBlock.data.tenkanPeriod || 9}
              onChange={handleInputChange}
              min={1}
              max={50}
              parser={(v) => parseInt(v)}
            />
            <NumberInput
              label="Kijun Period"
              tooltip="Base line period (typically 26)"
              field="kijunPeriod"
              value={selectedBlock.data.kijunPeriod || 26}
              onChange={handleInputChange}
              min={1}
              max={100}
              parser={(v) => parseInt(v)}
            />
            <NumberInput
              label="Senkou B Period"
              tooltip="Lagging span period (typically 52)"
              field="senkouBPeriod"
              value={selectedBlock.data.senkouBPeriod || 52}
              onChange={handleInputChange}
              min={1}
              max={200}
              parser={(v) => parseInt(v)}
            />
          </div>

          <NumberInput
            label="Displacement"
            tooltip="Forward displacement for cloud (typically 26)"
            field="displacement"
            value={selectedBlock.data.displacement || 26}
            onChange={handleInputChange}
            min={1}
            max={100}
            parser={(v) => parseInt(v)}
          />

          <div className="space-y-3">
            <CheckboxInput
              label="Show Cloud"
              tooltip="Display Ichimoku cloud"
              field="showCloud"
              value={selectedBlock.data.showCloud !== false}
              onChange={handleInputChange}
            />
            <CheckboxInput
              label="Show Chikou Span"
              tooltip="Display Chikou span (lagging line)"
              field="showChikou"
              value={selectedBlock.data.showChikou !== false}
              onChange={handleInputChange}
            />
          </div>
        </div>
      )}

      {/* SuperTrend specific fields */}
      {selectedBlock.data.indicator === 'supertrend' && (
        <div className="grid grid-cols-2 gap-4">
          <NumberInput
            label="ATR Period"
            tooltip="ATR period for SuperTrend calculation"
            field="atrPeriod"
            value={selectedBlock.data.atrPeriod || 10}
            onChange={handleInputChange}
            min={1}
            max={50}
            parser={(v) => parseInt(v)}
          />
          <NumberInput
            label="Multiplier"
            tooltip="ATR multiplier for SuperTrend bands"
            field="multiplier"
            value={selectedBlock.data.multiplier || 3}
            onChange={handleInputChange}
            min={1}
            max={10}
            step={0.5}
          />
        </div>
      )}
    </div>
  );

  // Time Filter fields
  const renderTimeFilterFields = () => (
    <div className="space-y-4">
      <div className="bg-yellow-50 p-3 rounded-md border border-yellow-200">
        <h4 className="text-sm font-medium text-yellow-800 mb-2">
          TSL Time Filter
        </h4>
        <p className="text-xs text-yellow-700 leading-relaxed">
          Filter trades by time sessions and schedules. Must flow through
          Expression or Logic Gate blocks.
        </p>
      </div>

      <SelectInput
        label="Filter Type"
        tooltip="Type of time filtering to apply"
        field="filterType"
        value={selectedBlock.data.filterType}
        onChange={handleInputChange}
        hasError={getFieldValidation('filterType').hasError}
        hasWarning={getFieldValidation('filterType').hasWarning}
        options={[
          { value: 'session', label: 'Trading Session Filter' },
          { value: 'time_range', label: 'Time Range Filter' },
          { value: 'day_of_week', label: 'Day of Week Filter' },
          { value: 'combined', label: 'Combined Filter' },
        ]}
        defaultValue="session"
      />

      {(selectedBlock.data.filterType === 'session' ||
        selectedBlock.data.filterType === 'combined') && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Trading Sessions
          </label>
          <div className="space-y-2">
            {/*
              S8c (2026-06-03) — F5 fix: read/write the engine-canonical
              `sessions` boolean record (engine: graph.ts:196). The
              previous handler wrote to `sessionFilter.sessions[]` (an
              array of session keys) which the engine ignored. The
              two overlap options ('overlap_eu_us', 'overlap_asia_eu')
              were never recognized by evalTimeFilter (it dispatches
              ONLY on asian/european/american boolean keys), so
              keeping them in the checkbox list would still produce
              dead UI surface. They've been removed; users who want
              an EU/US overlap can select both European and American
              (the engine OR-combines the booleans).
            */}
            {[
              { key: 'asian', label: 'Asian Session (Tokyo)' },
              { key: 'european', label: 'European Session (London)' },
              { key: 'american', label: 'American Session (New York)' },
            ].map((session) => {
              const sessions =
                (selectedBlock.data.sessions as
                  | Record<string, boolean>
                  | undefined) ?? {};
              return (
                <label key={session.key} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={!!sessions[session.key]}
                    onChange={(e) => {
                      const newSessions: Record<string, boolean> = {
                        ...sessions,
                        [session.key]: e.target.checked,
                      };
                      handleInputChange('sessions', newSessions as any);
                    }}
                    className="mr-2"
                  />
                  <span className="text-sm">{session.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {(selectedBlock.data.filterType === 'time_range' ||
        selectedBlock.data.filterType === 'combined') && (
        <div className="grid grid-cols-2 gap-4">
          <TextInput
            label="Start Time"
            tooltip="Start time for trading (HH:MM format)"
            field="startTime"
            value={selectedBlock.data.startTime}
            onChange={handleInputChange}
            placeholder="09:00"
          />
          <TextInput
            label="End Time"
            tooltip="End time for trading (HH:MM format)"
            field="endTime"
            value={selectedBlock.data.endTime}
            onChange={handleInputChange}
            placeholder="17:00"
          />
          {/*
            S10e (2026-06-03) — F25 fix. blockMinutesFromOpen +
            blockMinutesBeforeClose. Pine's "avoidOpenClose" gate:
            mask out the first/last N minutes of the session
            window. Engine reads at graph.ts:131, 237. Pre-S10e
            JSON-only.
          */}
          <NumberInput
            label="Block first N minutes after open"
            tooltip="Refuse entries within the first N minutes of the time window (Pine's blockMinutesFromOpen). 0 = disabled."
            field="blockMinutesFromOpen"
            value={(selectedBlock.data as Record<string, unknown>).blockMinutesFromOpen as number | undefined}
            onChange={handleInputChange}
            min={0}
            step={1}
            parser={(v) => parseInt(v)}
          />
          <NumberInput
            label="Block last N minutes before close"
            tooltip="Refuse entries within the last N minutes before the time window ends (Pine's blockMinutesBeforeClose). 0 = disabled."
            field="blockMinutesBeforeClose"
            value={(selectedBlock.data as Record<string, unknown>).blockMinutesBeforeClose as number | undefined}
            onChange={handleInputChange}
            min={0}
            step={1}
            parser={(v) => parseInt(v)}
          />
        </div>
      )}

      {/*
        S10e — F26 fix. noWeekends global Sat/Sun gate. Engine
        reads at graph.ts:136 and short-circuits BEFORE the
        filterType dispatch — so it applies regardless of which
        filter mode is active. Renders outside the filter-type
        conditionals so a session-mode user can also enable it.
      */}
      <CheckboxInput
        label="No weekends (block Sat / Sun)"
        tooltip="Reject Saturday and Sunday candles regardless of the filter type above. Engine reads at graph.ts:136."
        field="noWeekends"
        value={(selectedBlock.data as Record<string, unknown>).noWeekends as boolean | undefined}
        onChange={handleInputChange}
      />

      {(selectedBlock.data.filterType === 'day_of_week' ||
        selectedBlock.data.filterType === 'combined') && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Trading Days
          </label>
          <div className="grid grid-cols-2 gap-2">
            {[
              { key: 'monday', label: 'Monday' },
              { key: 'tuesday', label: 'Tuesday' },
              { key: 'wednesday', label: 'Wednesday' },
              { key: 'thursday', label: 'Thursday' },
              { key: 'friday', label: 'Friday' },
              { key: 'saturday', label: 'Saturday' },
              { key: 'sunday', label: 'Sunday' },
            ].map((day) => (
              <label key={day.key} className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedBlock.data.tradingDays?.[day.key] || false}
                  onChange={(e) => {
                    handleInputChange('tradingDays', {
                      ...selectedBlock.data?.tradingDays,
                      [day.key]: e.target.checked,
                    } as any);
                  }}
                  className="mr-2"
                />
                <span className="text-sm">{day.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <SelectInput
        label="Timezone"
        tooltip="Timezone for time-based filtering"
        field="timezone"
        value={selectedBlock.data.timezone}
        onChange={handleInputChange}
        options={[
          { value: 'UTC', label: 'UTC' },
          { value: 'America/New_York', label: 'New York (EST/EDT)' },
          { value: 'Europe/London', label: 'London (GMT/BST)' },
          { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
          { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
          { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
        ]}
        defaultValue="UTC"
      />
    </div>
  );

  // Logic Gate fields
  const renderLogicGateFields = () => (
    <div className="space-y-4">
      <div className="bg-indigo-50 p-3 rounded-md border border-indigo-200">
        <h4 className="text-sm font-medium text-indigo-800 mb-2">
          TSL Logic Gate
        </h4>
        <p className="text-xs text-indigo-700 leading-relaxed">
          Combine multiple signals using boolean logic operations. Can chain
          with other logic blocks.
        </p>
      </div>

      <SelectInput
        label="Logic Operation"
        tooltip="Boolean operation to apply to input signals"
        field="type"
        value={selectedBlock.data.type}
        onChange={handleInputChange}
        hasError={getFieldValidation('type').hasError}
        hasWarning={getFieldValidation('type').hasWarning}
        // S10l (2026-06-03) — F22 fix. Removed XNOR from the
        // dropdown — the engine's evalLogicGate (src/engine/
        // graph.ts:642) dispatches AND/OR/XOR/NAND/NOR only.
        // XNOR has no arm; picking it fell through to the
        // `default: r = false` branch, producing a permanently-
        // false gate. The user can author XNOR by using XOR
        // with `invert: true` (the existing Invert checkbox).
        options={[
          { value: 'AND', label: 'AND - All inputs must be true' },
          { value: 'OR', label: 'OR - Any input can be true' },
          { value: 'NAND', label: 'NAND - NOT (All inputs true)' },
          { value: 'NOR', label: 'NOR - NOT (Any input true)' },
          { value: 'XOR', label: 'XOR - Odd number of true inputs' },
        ]}
        defaultValue="AND"
      />

      <CheckboxInput
        label="Invert Output"
        tooltip="Invert the final result (NOT operation)"
        field="invert"
        value={selectedBlock.data.invert}
        onChange={handleInputChange}
      />

      {/* S4a (2026-06-02): "Advanced Options" section removed.
          The Fuzzy Logic checkbox + Threshold number-input promised a
          partial-truth gate mode the engine has never implemented —
          src/engine/graph.ts:evalLogicGate dispatches only on the
          AND/OR/XOR/NAND/NOR `type` field plus the `invert` toggle.
          Both inputs were dead UI surface. See
          docs/BLOCK_SETTINGS_AUDIT.md S4 section. */}
    </div>
  );

  // Enhanced Expression fields (already implemented in original)
  const renderExpressionFields = () => {
    const connectedBlocks = selectedBlock.connections.inputs
      .map((inputId) => blocks.find((b) => b.id === inputId))
      .filter((block): block is Block => block !== undefined);

    const priceDataBlocks = blocks.filter(
      (b) => b.type === BlockType.PRICE_DATA
    );

    return (
      <div className="space-y-4">
        <div className="bg-teal-50 p-3 rounded-md border border-teal-200">
          <h4 className="text-sm font-medium text-teal-800 mb-2">
            TSL Expression Logic Block
          </h4>
          <p className="text-xs text-teal-700 leading-relaxed">
            This block performs logical comparisons and can connect to other
            logic blocks or terminal blocks. It's a key component in the
            structured signal flow.
          </p>
        </div>

        <div className="p-3 bg-blue-50 rounded-md border border-blue-200">
          <h4 className="text-sm font-medium text-blue-800 mb-3">
            Left Operand
          </h4>

          <SelectInput
            label="Source Type"
            tooltip="Source for the left side of the comparison"
            field="leftSource"
            value={selectedBlock.data.leftSource}
            onChange={handleInputChange}
            options={[
              { value: 'input', label: 'Connected Block Signal' },
              { value: 'static', label: 'Static Value' },
              { value: 'price', label: 'Global Price Data' },
              { value: 'price_data', label: 'Price Data Block' },
              ...(fundamentalsDimensionGate().enabled ? [{ value: 'feature', label: 'Fundamental (SF1)' }] : []),
            ]}
            defaultValue="input"
          />

          {fundamentalsDimensionGate().enabled && selectedBlock.data.leftSource === 'feature' && (
            <>
              <SelectInput
                label="Fundamental Metric"
                tooltip="Point-in-time fundamental (Sharadar SF1); aligned to bars by filing knowledge-date (no look-ahead)"
                field="leftFeature"
                value={selectedBlock.data.leftFeature}
                onChange={handleInputChange}
                options={FUNDAMENTAL_METRIC_OPTIONS}
                defaultValue="fund.pe"
              />
              <p className="mt-1 text-[11px] text-gray-500">Fundamental metrics use point-in-time SF1 data aligned by knowledge date.</p>
            </>
          )}

          {selectedBlock.data.leftSource === ExpressionSourceType.STATIC && (
            <NumberInput
              label="Static Value"
              tooltip="Static numeric value for left operand"
              field="leftValue"
              value={selectedBlock.data.leftValue}
              onChange={handleInputChange}
              step={0.01}
            />
          )}

          {selectedBlock.data.leftSource === ExpressionSourceType.INPUT && (
            <>
              {connectedBlocks.length > 0 ? (
                <SelectInput
                  label="Input Block"
                  tooltip="Which connected block to use for the left operand"
                  field="leftInputBlockId"
                  value={selectedBlock.data.leftInputBlockId}
                  onChange={handleInputChange}
                  options={connectedBlocks.map((block) => ({
                    value: block.id,
                    label: `${block.data.name || 'Unnamed'} (${block.type})`,
                  }))}
                />
              ) : (
                <div className="p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-xs text-yellow-700">
                    ⚠ No connected blocks available. Connect indicator or data
                    blocks to this expression.
                  </p>
                </div>
              )}
            </>
          )}

          {selectedBlock.data.leftSource === ExpressionSourceType.PRICE && (
            <SelectInput
              label="Price Source"
              tooltip="Which price data to use from the strategy's global settings"
              field="leftPriceSource"
              value={selectedBlock.data.leftPriceSource}
              onChange={handleInputChange}
              options={[
                { value: 'open', label: 'Open' },
                { value: 'high', label: 'High' },
                { value: 'low', label: 'Low' },
                { value: 'close', label: 'Close' },
                { value: 'hl2', label: 'HL2 (High + Low) / 2' },
                { value: 'hlc3', label: 'HLC3 (High + Low + Close) / 3' },
                {
                  value: 'ohlc4',
                  label: 'OHLC4 (Open + High + Low + Close) / 4',
                },
              ]}
              defaultValue="close"
            />
          )}

          {selectedBlock.data.leftSource ===
            ExpressionSourceType.PRICE_DATA && (
            <>
              {priceDataBlocks.length > 0 ? (
                <>
                  <SelectInput
                    label="Price Data Block"
                    tooltip="Which price data block to use (must be connected)"
                    field="leftPriceDataBlockId"
                    value={selectedBlock.data.leftPriceDataBlockId}
                    onChange={handleInputChange}
                    options={priceDataBlocks
                      .filter((block) =>
                        connectedBlocks.some((cb) => cb.id === block.id)
                      )
                      .map((block) => ({
                        value: block.id,
                        // S4f (2026-06-02): `${block.data.symbol}` dropped
                        // from the label since the symbol field has been
                        // removed. The block name + timeframe disambiguate
                        // adequately. See audit doc.
                        label: `${block.data.name || 'Price Data'} (${block.data.timeframe})`,
                      }))}
                  />

                  <SelectInput
                    label="Price Source"
                    tooltip="Which price value to extract from the price data block"
                    field="leftPriceSource"
                    value={selectedBlock.data.leftPriceSource}
                    onChange={handleInputChange}
                    options={[
                      { value: 'open', label: 'Open' },
                      { value: 'high', label: 'High' },
                      { value: 'low', label: 'Low' },
                      { value: 'close', label: 'Close' },
                      { value: 'hl2', label: 'HL2' },
                      { value: 'hlc3', label: 'HLC3' },
                      { value: 'ohlc4', label: 'OHLC4' },
                    ]}
                    defaultValue="close"
                  />
                </>
              ) : (
                <div className="p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-xs text-yellow-700">
                    ⚠ No Price Data blocks available. Add and connect a Price
                    Data block to use this option.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <SelectInput
          label="Comparison Operator"
          tooltip="How to compare the left and right operands"
          field="operator"
          value={selectedBlock.data.operator}
          onChange={handleInputChange}
          hasError={getFieldValidation('operator').hasError}
          hasWarning={getFieldValidation('operator').hasWarning}
          options={[
            { value: '>', label: 'Greater Than (>)' },
            { value: '<', label: 'Less Than (<)' },
            { value: '>=', label: 'Greater Than or Equal (>=)' },
            { value: '<=', label: 'Less Than or Equal (<=)' },
            { value: '==', label: 'Equal To (==)' },
            { value: '!=', label: 'Not Equal To (!=)' },
            { value: 'crosses_above', label: 'Crosses Above' },
            { value: 'crosses_below', label: 'Crosses Below' },
            { value: 'inside_range', label: 'Inside Range' },
            { value: 'outside_range', label: 'Outside Range' },
            { value: 'rising', label: 'Rising (Positive Slope)' },
            { value: 'falling', label: 'Falling (Negative Slope)' },
          ]}
          defaultValue=">"
        />

        <div className="p-3 bg-green-50 rounded-md border border-green-200">
          <h4 className="text-sm font-medium text-green-800 mb-3">
            Right Operand
          </h4>

          {/*
            S10a (2026-06-03) — F17 fix. Pre-S10a only the 'static'
            source was rendered for the right operand; everything
            else (input / price / price_data) had only a placeholder
            comment `{/* Similar right operand implementation as left
            operand *\/}`. The most common Expression pattern
            ("indicator > another indicator") was unauthorable.
            Now mirrors the left-operand form completely.

            S10b (2026-06-03) — F18 fix. The 'inside_range' and
            'outside_range' operators were in the dropdown but had no
            corresponding source options for static or dynamic ranges.
            Added 'range_static' and 'range_input' source options
            (gated to range operators), the rightRange.low/high
            inputs for static, and rightLowInputBlockId /
            rightHighInputBlockId selects for dynamic.
          */}
          <SelectInput
            label="Source Type"
            tooltip="Source for the right side of the comparison"
            field="rightSource"
            value={selectedBlock.data.rightSource}
            onChange={handleInputChange}
            options={(() => {
              const base = [
                { value: 'input', label: 'Connected Block Signal' },
                { value: 'static', label: 'Static Value' },
                { value: 'price', label: 'Global Price Data' },
                { value: 'price_data', label: 'Price Data Block' },
                ...(fundamentalsDimensionGate().enabled ? [{ value: 'feature', label: 'Fundamental (SF1)' }] : []),
              ];
              // S10b — range sources only make sense for the two
              // range operators. Show them only when applicable so
              // the dropdown doesn't carry dead options for users
              // doing simple comparisons.
              const op = String(selectedBlock.data.operator ?? '>');
              if (op === 'inside_range' || op === 'outside_range') {
                base.push(
                  { value: 'range_static', label: 'Static Range (low / high)' },
                  { value: 'range_input', label: 'Dynamic Range (two connected blocks)' },
                );
              }
              return base;
            })()}
            defaultValue="static"
          />

          {fundamentalsDimensionGate().enabled && selectedBlock.data.rightSource === 'feature' && (
            <>
              <SelectInput
                label="Fundamental Metric"
                tooltip="Point-in-time fundamental (Sharadar SF1); aligned to bars by filing knowledge-date (no look-ahead)"
                field="rightFeature"
                value={selectedBlock.data.rightFeature}
                onChange={handleInputChange}
                options={FUNDAMENTAL_METRIC_OPTIONS}
                defaultValue="fund.pe"
              />
              <p className="mt-1 text-[11px] text-gray-500">Fundamental metrics use point-in-time SF1 data aligned by knowledge date.</p>
            </>
          )}

          {selectedBlock.data.rightSource === 'static' && (
            <NumberInput
              label="Static Value"
              tooltip="Static numeric value for right operand"
              field="rightValue"
              value={selectedBlock.data.rightValue}
              onChange={handleInputChange}
              step={0.01}
            />
          )}

          {selectedBlock.data.rightSource === ExpressionSourceType.INPUT && (
            <>
              {connectedBlocks.length > 0 ? (
                <SelectInput
                  label="Input Block"
                  tooltip="Which connected block to use for the right operand"
                  field="rightInputBlockId"
                  value={selectedBlock.data.rightInputBlockId}
                  onChange={handleInputChange}
                  options={connectedBlocks.map((block) => ({
                    value: block.id,
                    label: `${block.data.name || 'Unnamed'} (${block.type})`,
                  }))}
                />
              ) : (
                <div className="p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-xs text-yellow-700">
                    ⚠ No connected blocks available. Connect indicator or data
                    blocks to this expression.
                  </p>
                </div>
              )}
            </>
          )}

          {selectedBlock.data.rightSource === ExpressionSourceType.PRICE && (
            <SelectInput
              label="Price Source"
              tooltip="Which price data to use from the strategy's global settings"
              field="rightPriceSource"
              value={selectedBlock.data.rightPriceSource}
              onChange={handleInputChange}
              options={[
                { value: 'open', label: 'Open' },
                { value: 'high', label: 'High' },
                { value: 'low', label: 'Low' },
                { value: 'close', label: 'Close' },
                { value: 'hl2', label: 'HL2 (High + Low) / 2' },
                { value: 'hlc3', label: 'HLC3 (High + Low + Close) / 3' },
                { value: 'ohlc4', label: 'OHLC4 (Open + High + Low + Close) / 4' },
              ]}
              defaultValue="close"
            />
          )}

          {selectedBlock.data.rightSource === ExpressionSourceType.PRICE_DATA && (
            <>
              {priceDataBlocks.length > 0 ? (
                <>
                  <SelectInput
                    label="Price Data Block"
                    tooltip="Which price data block to use (must be connected)"
                    field="rightPriceDataBlockId"
                    value={selectedBlock.data.rightPriceDataBlockId}
                    onChange={handleInputChange}
                    options={priceDataBlocks
                      .filter((block) =>
                        connectedBlocks.some((cb) => cb.id === block.id)
                      )
                      .map((block) => ({
                        value: block.id,
                        label: `${block.data.name || 'Price Data'} (${block.data.timeframe})`,
                      }))}
                  />
                  <SelectInput
                    label="Price Source"
                    tooltip="Which price value to extract from the price data block"
                    field="rightPriceSource"
                    value={selectedBlock.data.rightPriceSource}
                    onChange={handleInputChange}
                    options={[
                      { value: 'open', label: 'Open' },
                      { value: 'high', label: 'High' },
                      { value: 'low', label: 'Low' },
                      { value: 'close', label: 'Close' },
                      { value: 'hl2', label: 'HL2' },
                      { value: 'hlc3', label: 'HLC3' },
                      { value: 'ohlc4', label: 'OHLC4' },
                    ]}
                    defaultValue="close"
                  />
                </>
              ) : (
                <div className="p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-xs text-yellow-700">
                    ⚠ No Price Data blocks available. Add and connect a Price
                    Data block to use this option.
                  </p>
                </div>
              )}
            </>
          )}

          {/* S10b — Static range operand. Reads rightRange.{low,high}
              from data; the engine consumes both at graph.ts:572. */}
          {selectedBlock.data.rightSource === 'range_static' && (
            <div className="grid grid-cols-2 gap-2">
              <NumberInput
                label="Range Low"
                tooltip="Lower bound of the static range (inclusive by default)"
                field="rightRangeLow"
                value={(selectedBlock.data.rightRange as { low?: number; high?: number } | undefined)?.low}
                onChange={(_field, value) =>
                  handleInputChange('rightRange', {
                    ...(selectedBlock.data.rightRange ?? {}),
                    low: value,
                  } as any)
                }
                step={0.01}
              />
              <NumberInput
                label="Range High"
                tooltip="Upper bound of the static range (inclusive by default)"
                field="rightRangeHigh"
                value={(selectedBlock.data.rightRange as { low?: number; high?: number } | undefined)?.high}
                onChange={(_field, value) =>
                  handleInputChange('rightRange', {
                    ...(selectedBlock.data.rightRange ?? {}),
                    high: value,
                  } as any)
                }
                step={0.01}
              />
            </div>
          )}

          {/* S10b — Dynamic range operand. Reads rightLowInputBlockId
              + rightHighInputBlockId from data; the engine consumes
              both at graph.ts:483-485 (range_input source). */}
          {selectedBlock.data.rightSource === 'range_input' && (
            <>
              {connectedBlocks.length >= 2 ? (
                <>
                  <SelectInput
                    label="Low Bound Block"
                    tooltip="Connected block whose value series is the dynamic low bound"
                    field="rightLowInputBlockId"
                    value={(selectedBlock.data as Record<string, unknown>).rightLowInputBlockId as string | undefined}
                    onChange={handleInputChange}
                    options={connectedBlocks.map((block) => ({
                      value: block.id,
                      label: `${block.data.name || 'Unnamed'} (${block.type})`,
                    }))}
                  />
                  <SelectInput
                    label="High Bound Block"
                    tooltip="Connected block whose value series is the dynamic high bound"
                    field="rightHighInputBlockId"
                    value={(selectedBlock.data as Record<string, unknown>).rightHighInputBlockId as string | undefined}
                    onChange={handleInputChange}
                    options={connectedBlocks.map((block) => ({
                      value: block.id,
                      label: `${block.data.name || 'Unnamed'} (${block.type})`,
                    }))}
                  />
                </>
              ) : (
                <div className="p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-xs text-yellow-700">
                    ⚠ Dynamic ranges need TWO connected blocks (one for the low
                    bound, one for the high bound). Connect two blocks first.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
          <h4 className="text-sm font-medium text-gray-800 mb-3">
            Advanced Options
          </h4>

          {/*
            S10b — F21 fix. Range-bounds inclusivity, only meaningful
            for inside_range / outside_range. Default {low: true,
            high: true} matches engine default (graph.ts:531-533).
          */}
          {(selectedBlock.data.operator === 'inside_range' ||
            selectedBlock.data.operator === 'outside_range') && (
            <div className="grid grid-cols-2 gap-2 mb-3">
              <CheckboxInput
                label="Low bound inclusive (≥/<)"
                tooltip="When checked, low bound uses ≥ (or > for outside). When unchecked, strict >."
                field="rangeBoundsInclusiveLow"
                value={((selectedBlock.data.rangeBoundsInclusive as { low?: boolean } | undefined)?.low ?? true) as boolean}
                onChange={(_field, value) =>
                  handleInputChange('rangeBoundsInclusive', {
                    ...((selectedBlock.data.rangeBoundsInclusive as Record<string, unknown>) ?? {}),
                    low: value,
                  } as any)
                }
              />
              <CheckboxInput
                label="High bound inclusive (≤/>)"
                tooltip="When checked, high bound uses ≤ (or < for outside). When unchecked, strict <."
                field="rangeBoundsInclusiveHigh"
                value={((selectedBlock.data.rangeBoundsInclusive as { high?: boolean } | undefined)?.high ?? true) as boolean}
                onChange={(_field, value) =>
                  handleInputChange('rangeBoundsInclusive', {
                    ...((selectedBlock.data.rangeBoundsInclusive as Record<string, unknown>) ?? {}),
                    high: value,
                  } as any)
                }
              />
            </div>
          )}

          {/*
            S10b — F19 fix. risingFallingPeriod controls the lookback
            for the `rising` / `falling` slope operators (engine reads
            at graph.ts:537). Pre-S10b only the operator dropdown
            offered these but the lookback was stuck at the default
            of 1. Gated so it only appears when relevant.
          */}
          {(selectedBlock.data.operator === 'rising' ||
            selectedBlock.data.operator === 'falling') && (
            <NumberInput
              label="Rising/Falling Lookback (bars)"
              tooltip="How many bars back to compare to determine the slope. Default 1 = current vs previous bar."
              field="risingFallingPeriod"
              value={(selectedBlock.data as Record<string, unknown>).risingFallingPeriod as number | undefined}
              onChange={handleInputChange}
              min={1}
              parser={(v) => parseInt(v)}
            />
          )}

          <NumberInput
            label="Persistence (bars)"
            tooltip="How many bars the condition must hold (consecutive or within window per the type below)"
            field="persistence"
            value={selectedBlock.data.persistence?.required}
            onChange={(_field, value) =>
              handleInputChange('persistence', {
                ...selectedBlock.data.persistence,
                required: value,
              } as any)
            }
            min={1}
            parser={(v) => parseInt(v)}
          />

          {/*
            S10b — F20 fix. Pre-S10b only persistence.required was
            UI-writable; persistence.type and persistence.period
            (the "N within window" mode) were JSON-only. Engine
            reads all three at graph.ts:508.
          */}
          {(selectedBlock.data.persistence?.required ?? 0) > 1 && (
            <>
              <SelectInput
                label="Persistence Mode"
                tooltip="consecutive = N bars in a row; within_period = N bars within the lookback window"
                field="persistenceType"
                value={selectedBlock.data.persistence?.type ?? 'consecutive'}
                onChange={(_field, value) =>
                  handleInputChange('persistence', {
                    ...selectedBlock.data.persistence,
                    type: value,
                  } as any)
                }
                options={[
                  { value: 'consecutive', label: 'Consecutive bars' },
                  { value: 'within_period', label: 'N within window' },
                ]}
                defaultValue="consecutive"
              />
              {selectedBlock.data.persistence?.type === 'within_period' && (
                <NumberInput
                  label="Lookback Window (bars)"
                  tooltip="Window size for within_period mode — how many recent bars to look back over"
                  field="persistencePeriod"
                  value={selectedBlock.data.persistence?.period}
                  onChange={(_field, value) =>
                    handleInputChange('persistence', {
                      ...selectedBlock.data.persistence,
                      period: value,
                    } as any)
                  }
                  min={1}
                  parser={(v) => parseInt(v)}
                />
              )}
            </>
          )}

          <CheckboxInput
            label="Invert result (NOT operation)"
            tooltip="Reverse the boolean result of the expression"
            field="invert"
            value={selectedBlock.data.invert}
            onChange={handleInputChange}
          />
        </div>
      </div>
    );
  };

  // TSL Pattern Recognition rendering (3 separate functions from original file)
  const renderChartPatternsFields = () => (
    <div className="space-y-4">
      <div className="bg-rose-50 p-3 rounded-md border border-rose-200">
        <h4 className="text-sm font-medium text-rose-800 mb-2">
          TSL Chart Pattern Detection
        </h4>
        <p className="text-xs text-rose-700 leading-relaxed">
          Detect chart patterns like triangles, flags, and reversals. Signals
          must flow through Expression or Logic Gate blocks before reaching
          terminal blocks.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Chart Patterns
        </label>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {/* S6b (2026-06-02) — removed the four unimplemented options
              `flag`, `pennant`, `cup_handle`, `trendline_break`. The
              src/engine/chartPatterns.ts file header explicitly says
              these are intentionally NOT implemented because they're
              fuzzy shape-detection problems that benefit from per-
              strategy tuning; the TSL spec steers users to a
              `blockType: 'pattern'` CUSTOM block for these. Selecting
              them in the dropdown silently produced no signals — that
              is the exact spec-mismatch the user asked to clean up.
              See docs/BLOCK_SETTINGS_AUDIT.md S6b section. */}
          {[
            {
              pattern: 'support_resistance_break',
              name: 'Support/Resistance Break',
              reliability: 'High',
            },
            {
              pattern: 'head_shoulders',
              name: 'Head & Shoulders',
              reliability: 'High',
            },
            {
              pattern: 'inverse_head_shoulders',
              name: 'Inverse Head & Shoulders',
              reliability: 'High',
            },
            {
              pattern: 'triangle_ascending',
              name: 'Ascending Triangle',
              reliability: 'High',
            },
            {
              pattern: 'triangle_descending',
              name: 'Descending Triangle',
              reliability: 'High',
            },
            {
              pattern: 'triangle_symmetrical',
              name: 'Symmetrical Triangle',
              reliability: 'Medium',
            },
            { pattern: 'double_top', name: 'Double Top', reliability: 'High' },
            {
              pattern: 'double_bottom',
              name: 'Double Bottom',
              reliability: 'High',
            },
          ].map((item) => (
            <label
              key={item.pattern}
              className="flex items-center p-2 border rounded hover:bg-gray-50"
            >
              <input
                type="checkbox"
                className="mr-2"
                checked={
                  selectedBlock.data.chartPatterns?.includes(item.pattern) ||
                  false
                }
                onChange={(e) => {
                  // S10f (2026-06-03) — F23 fix. Pre-S10f this handler
                  // unconditionally appended `item.pattern` to the
                  // chartPatterns array, so unchecking the box was a
                  // no-op and re-checking added a duplicate entry.
                  // Toggle semantics: when checked, append if not
                  // already present; when unchecked, filter out.
                  const currentPatterns: string[] =
                    selectedBlock.data.chartPatterns || [];
                  const next: string[] = e.target.checked
                    ? currentPatterns.includes(item.pattern)
                      ? currentPatterns
                      : [...currentPatterns, item.pattern]
                    : currentPatterns.filter((p) => p !== item.pattern);
                  handleInputChange('chartPatterns', next as any);
                }}
              />
              <div className="flex-1">
                <div className="text-sm font-medium">{item.name}</div>
                <div
                  className={`text-xs ${
                    item.reliability === 'High'
                      ? 'text-green-600'
                      : item.reliability === 'Medium'
                      ? 'text-yellow-600'
                      : 'text-red-600'
                  }`}
                >
                  {item.reliability} Reliability
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <SelectInput
        label="Sensitivity"
        tooltip="Higher = more patterns detected (more false positives)"
        field="sensitivity"
        value={selectedBlock.data.sensitivity}
        onChange={handleInputChange}
        options={[
          { value: 'strict', label: 'Strict (Fewer, higher quality)' },
          { value: 'medium', label: 'Medium (Balanced)' },
          { value: 'loose', label: 'Loose (More patterns, lower quality)' },
        ]}
        defaultValue="medium"
      />

      <NumberInput
        label="Minimum Pattern Size (bars)"
        tooltip="Minimum number of bars required for pattern"
        field="minPatternSize"
        value={selectedBlock.data.minPatternSize}
        onChange={handleInputChange}
        min={5}
        parser={(v) => parseInt(v)}
      />

      <NumberInput
        label="Confirmation Period (bars)"
        tooltip="Number of bars to wait for pattern confirmation"
        field="confirmationBars"
        value={selectedBlock.data.confirmationBars}
        onChange={handleInputChange}
        min={1}
        parser={(v) => parseInt(v)}
      />
    </div>
  );

  const renderCandlestickPatternsFields = () => (
    <div className="space-y-4">
      <div className="bg-pink-50 p-3 rounded-md border border-pink-200">
        <h4 className="text-sm font-medium text-pink-800 mb-2">
          TSL Candlestick Pattern Detection
        </h4>
        <p className="text-xs text-pink-700 leading-relaxed">
          Detect traditional Japanese candlestick patterns for reversal and
          continuation signals. Must flow through logic blocks to terminal
          blocks.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Candlestick Patterns
        </label>
        <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
          {[
            { pattern: 'doji', name: 'Doji', reliability: 'Medium' },
            { pattern: 'hammer', name: 'Hammer', reliability: 'High' },
            {
              pattern: 'hanging_man',
              name: 'Hanging Man',
              reliability: 'High',
            },
            {
              pattern: 'shooting_star',
              name: 'Shooting Star',
              reliability: 'High',
            },
            { pattern: 'engulfing', name: 'Engulfing', reliability: 'High' },
            { pattern: 'harami', name: 'Harami', reliability: 'Medium' },
            {
              pattern: 'morning_star',
              name: 'Morning Star',
              reliability: 'High',
            },
            {
              pattern: 'evening_star',
              name: 'Evening Star',
              reliability: 'High',
            },
            {
              pattern: 'three_white_soldiers',
              name: 'Three White Soldiers',
              reliability: 'High',
            },
            {
              pattern: 'three_black_crows',
              name: 'Three Black Crows',
              reliability: 'High',
            },
          ].map((item) => (
            <label
              key={item.pattern}
              className="flex items-center p-2 border rounded hover:bg-gray-50"
            >
              <input
                type="checkbox"
                className="mr-2"
                checked={
                  selectedBlock.data.candlestickPatterns?.includes(
                    item.pattern
                  ) || false
                }
                onChange={(e) => {
                  // S10f (2026-06-03) — F24 fix. Same broken-toggle
                  // pattern as the chart-pattern checkbox: pre-S10f
                  // unchecking did nothing and re-checking duplicated
                  // the entry. Toggle semantics restored.
                  const currentPatterns: string[] =
                    selectedBlock.data.candlestickPatterns || [];
                  const next: string[] = e.target.checked
                    ? currentPatterns.includes(item.pattern)
                      ? currentPatterns
                      : [...currentPatterns, item.pattern]
                    : currentPatterns.filter((p) => p !== item.pattern);
                  handleInputChange('candlestickPatterns', next as any);
                }}
              />
              <div className="flex-1">
                <div className="text-sm font-medium">{item.name}</div>
                <div
                  className={`text-xs ${
                    item.reliability === 'High'
                      ? 'text-green-600'
                      : item.reliability === 'Medium'
                      ? 'text-yellow-600'
                      : 'text-red-600'
                  }`}
                >
                  {item.reliability} Reliability
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* S6c (2026-06-02) — Sensitivity is DOJI-SPECIFIC.
          src/engine/graph.ts:338 derives `dojiThreshold` from this field
          (strict → 0.05, medium → 0.10, loose → 0.20) and applies it
          inside `isDoji(c, dojiThreshold)`. Every other candlestick
          detector (hammer, engulfing, harami, morning_star, etc.) uses
          hard-coded body / shadow ratios independent of sensitivity.

          Pre-S6c the field was labeled generically as "Sensitivity"
          with a tooltip implying it affected detection quality across
          all patterns — misleading. S6c restricts the input's
          visibility to when `doji` is in the selected pattern set, and
          relabels the input + tooltip to make the doji-only scope
          explicit. See docs/BLOCK_SETTINGS_AUDIT.md S6c section.

          The field name stays as `sensitivity` for backward-compat
          (existing strategies have it stored under that key; the
          engine still reads it). A future S6 follow-on could rename to
          `dojiBodyThreshold` if we accept a save-data migration.

          When the user has NOT selected doji, the input doesn't render
          and the engine's `dojiThreshold` default of 0.10 (medium)
          applies — but it's a no-op because no doji detection runs. */}
      {(selectedBlock.data.candlestickPatterns ?? []).includes('doji') && (
        <SelectInput
          label="Doji body threshold (sensitivity)"
          tooltip="Sets the maximum body / range ratio for a candle to count as a doji. Strict = 0.05 (only near-perfect dojis), Medium = 0.10 (standard), Loose = 0.20 (any small-bodied candle). Affects ONLY doji detection — every other candlestick pattern (hammer, engulfing, etc.) uses fixed body / shadow ratios and ignores this setting."
          field="sensitivity"
          value={selectedBlock.data.sensitivity}
          onChange={handleInputChange}
          options={[
            { value: 'strict', label: 'Strict — body ≤ 5% of range' },
            { value: 'medium', label: 'Medium — body ≤ 10% of range (default)' },
            { value: 'loose',  label: 'Loose — body ≤ 20% of range' },
          ]}
          defaultValue="medium"
        />
      )}

      <NumberInput
        label="Confirmation Period (bars)"
        tooltip="Number of bars to wait for pattern confirmation"
        field="confirmationBars"
        value={selectedBlock.data.confirmationBars}
        onChange={handleInputChange}
        min={1}
        parser={(v) => parseInt(v)}
      />
    </div>
  );

  // Reserved render helper for a custom pattern detection panel —
  // not currently wired into the active builder UI but kept here as
  // a known scaffold for future expansion. Underscore-prefixed +
  // void to silence the unused-var check without removing the
  // helper.
  const _renderCustomPatternFields = () => (
    <div className="space-y-4">
      <div className="bg-purple-50 p-3 rounded-md border border-purple-200">
        <h4 className="text-sm font-medium text-purple-800 mb-2">
          TSL Custom Pattern Detection
        </h4>
        <p className="text-xs text-purple-700 leading-relaxed">
          Create custom pattern detection logic using JavaScript. Define custom
          patterns for unique market conditions or trading setups.
        </p>
      </div>

      <div>
        <TooltipLabel
          label="JavaScript Code"
          tooltip="Function that detects patterns using OHLCV candlestick data"
        />
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={() => setPreviewMode(!previewMode)}
            className="text-sm text-gray-600 hover:text-gray-700 flex items-center space-x-1"
          >
            {previewMode ? (
              <EyeOff className="w-4 h-4" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
            <span>{previewMode ? 'Hide' : 'Preview'}</span>
          </button>
        </div>

        {previewMode && (
          <div className="bg-gray-100 rounded-md p-3 max-h-32 overflow-y-auto">
            <pre className="text-xs text-gray-600 whitespace-pre-wrap">
              {selectedBlock.data.code || 'No pattern code defined yet'}
            </pre>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <NumberInput
          label="Period"
          tooltip="Number of bars to analyze for pattern detection"
          field="period"
          value={selectedBlock.data.period || 5}
          onChange={handleInputChange}
          min={2}
          max={50}
          parser={(v) => parseInt(v)}
        />

        <NumberInput
          label="Min Confidence"
          tooltip="Minimum confidence threshold for pattern detection (0-1)"
          field="minConfidence"
          value={selectedBlock.data.minConfidence || 0.6}
          onChange={handleInputChange}
          min={0}
          max={1}
          step={0.1}
        />
      </div>

      <SelectInput
        label="Sensitivity"
        tooltip="Pattern detection sensitivity level"
        field="sensitivity"
        value={selectedBlock.data.sensitivity}
        onChange={handleInputChange}
        options={[
          { value: 'strict', label: 'Strict (Higher Quality)' },
          { value: 'medium', label: 'Medium (Balanced)' },
          { value: 'loose', label: 'Loose (More Patterns)' },
        ]}
        defaultValue="medium"
      />

      <NumberInput
        label="Confirmation Bars"
        tooltip="Number of bars to wait for pattern confirmation"
        field="confirmationBars"
        value={selectedBlock.data.confirmationBars || 1}
        onChange={handleInputChange}
        min={0}
        max={10}
        parser={(v) => parseInt(v)}
      />
    </div>
  );
  void _renderCustomPatternFields;

  // Enhanced TSL terminal block rendering (from original file)
  const renderEntryFields = () => (
    <div className="space-y-4">
      <div className="bg-green-50 p-3 rounded-md border border-green-200">
        <h4 className="text-sm font-medium text-green-800 mb-2">
          TSL Entry Terminal Block
        </h4>
        <p className="text-xs text-green-700 leading-relaxed">
          Entry blocks generate trade signals and orders. They must receive input
          from Expression or Logic Gate blocks only. Each entry block can trade
          either long or short positions.
        </p>
      </div>

      <TextInput
        label="Block Name"
        tooltip="Name for this entry block"
        field="name"
        value={selectedBlock.data.name}
        onChange={handleInputChange}
        placeholder="Long Entry"
      />

      <div className="grid grid-cols-2 gap-4">
        <SelectInput
          label="Entry Type"
          tooltip="Type of order to place"
          field="entryType"
          value={selectedBlock.data.entryType}
          onChange={handleInputChange}
          options={[
            { value: 'market', label: 'Market Order' },
            { value: 'limit', label: 'Limit Order' },
            { value: 'stop', label: 'Stop Order' },
            { value: 'stop_limit', label: 'Stop Limit Order' },
          ]}
          defaultValue="market"
        />

        <SelectInput
          label="Direction"
          tooltip="Trading direction for this entry block"
          field="direction"
          value={selectedBlock.data.direction}
          onChange={handleInputChange}
          options={[
            { value: 'long', label: 'Long Only' },
            { value: 'short', label: 'Short Only' },
          ]}
          defaultValue="long"
        />
      </div>

      <SelectInput
        label="Logic Operator"
        tooltip="How multiple input signals are combined"
        field="logic"
        value={selectedBlock.data.logic}
        onChange={handleInputChange}
        options={[
          { value: 'AND', label: 'All conditions must be true (AND)' },
          { value: 'OR', label: 'Any condition can be true (OR)' },
          { value: 'XOR', label: 'Exactly one condition true (XOR)' },
        ]}
        defaultValue="AND"
      />

      <CheckboxInput
        label="Use Strategy Defaults"
        tooltip="Use position sizing from Strategy Configuration block"
        field="useStrategyDefaults"
        value={selectedBlock.data.useStrategyDefaults !== false}
        onChange={handleInputChange}
      />

      {selectedBlock.data.useStrategyDefaults === false && (
        <div className="border-l-4 border-orange-400 pl-4 space-y-4">
          <h4 className="text-sm font-medium text-orange-800">
            Custom Position Sizing
          </h4>

          {/*
            S8f (2026-06-03) — Override banner. When the entry's
            `qtyCalculatorBlockId` points at a real qty_calculator
            custom block, the engine ignores the Quantity Type /
            Value / Risk Per Trade fields below at run time (see
            src/engine/sizing.ts:390-428: if (ctx.qtyCalculatorBlock)
            { ... return qty; } -- the method-switch path is never
            reached). Pre-S8f there was no UI signal for this: the
            user saw "Quantity Type: fixed, Quantity Value: 0.01"
            and a canvas edge to a calc block, with no indication
            of precedence. The banner names the calc block + lets
            the user jump to it; the disabled inputs visually
            communicate that the values are inert.

            Discoverability note inside the banner: the precedence
            mechanism is the `qtyCalculatorBlockId` POINTER on the
            entry's data, not the canvas edge -- closes the
            "edges vs runtime refs" mental-model gap (same point
            the Dependencies panel makes via its "Runtime refs"
            section header).
          */}
          {(() => {
            const shadow = findShadowingRef(selectedBlock, 'quantity_type', blocks);
            if (!shadow) return null;
            return (
              <div className="rounded border border-yellow-300 bg-yellow-50 p-3 text-xs text-yellow-900">
                <div className="font-semibold mb-1">
                  ⚠ Sizing overridden by qty_calculator block
                </div>
                <div className="mb-2">
                  The engine computes position size by running{' '}
                  <span className="font-mono">computeQty(ctx, params, helpers)</span>{' '}
                  on{' '}
                  <span className="font-semibold">{shadow.name}</span>{' '}
                  (id <span className="font-mono">...{shadow.refId.slice(-8)}</span>)
                  via this entry&apos;s <span className="font-mono">qtyCalculatorBlockId</span> pointer.
                  The Quantity Type / Quantity Value / Risk Per Trade fields below are
                  <span className="font-semibold"> ignored at run time</span>. To
                  re-enable them, <span className="font-semibold">delete the canvas edge</span> from
                  the qty_calculator block to this entry (S8j: removing the edge clears the{' '}
                  <span className="font-mono">qtyCalculatorBlockId</span> pointer).
                </div>
                <div className="flex items-center gap-3">
                  {typeof onSelectBlockId === 'function' && (
                    <button
                      type="button"
                      onClick={() => onSelectBlockId(shadow.refId)}
                      className="text-blue-600 hover:text-blue-800 hover:underline text-xs font-medium"
                      title={`Jump to ${shadow.name}`}
                    >
                      Jump to {shadow.name} →
                    </button>
                  )}
                  {/*
                    S10n (2026-06-03) — F28 fix. "Clear pointer"
                    button gives the user a direct way to remove
                    the qtyCalculatorBlockId without finding +
                    deleting the canvas edge. Useful when the
                    user wants to switch back to inline sizing or
                    re-wire to a different calc block via the
                    dropdown / edge.
                  */}
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(
                        `Clear the qtyCalculatorBlockId pointer on this entry? The inline Quantity Type / Value fields will become active again. The qty_calculator block (${shadow.name}) is NOT deleted — only the pointer is cleared.`
                      )) {
                        const data = { ...(selectedBlock.data ?? {}) } as Record<string, unknown>;
                        delete data.qtyCalculatorBlockId;
                        onUpdate({ ...selectedBlock, data: data as BlockData });
                      }
                    }}
                    className="text-red-600 hover:text-red-800 hover:underline text-xs font-medium"
                    title="Clear the runtime-ref pointer (re-enables inline sizing fields)"
                  >
                    Clear pointer
                  </button>
                </div>
              </div>
            );
          })()}

          {/*
            S8f — Compute once whether the runtime-ref override is
            active so the shadowed inputs render as disabled and
            visually muted. Stale qtyCalculatorBlockId pointers
            (no matching block) do NOT shadow — the engine falls
            through to the method-switch in that case, so the
            inline value IS the live one and the input stays
            editable.
          */}
          {(() => {
            const qtyShadowed = findShadowingRef(selectedBlock, 'quantity_type', blocks) != null;
            // We reuse the SAME helper for each field name so the
            // override registry stays the single source of truth.
            const qtyValueShadowed = findShadowingRef(selectedBlock, 'quantity_value', blocks) != null;
            const riskShadowed = findShadowingRef(selectedBlock, 'risk_per_trade', blocks) != null;
            const qt = String(selectedBlock.data.quantity_type ?? '');
            // S10i (2026-06-03) — F30 fix. risk_per_trade UI gated
            // to a BROADER set of engine-supported risk-based
            // methods: risk_based / atr_risk / pine_futures /
            // fixed_risk / kelly_criterion / volatility_adjusted.
            // Pre-S10i gated to RISK_BASED only.
            const RISK_METHODS = new Set<string>([
              QuantityType.RISK_BASED,
              'atr_risk',
              'pine_futures',
              'fixed_risk',
              'kelly_criterion',
              'volatility_adjusted',
            ]);
            return (
              <div className={qtyShadowed || qtyValueShadowed || riskShadowed ? 'opacity-60' : ''}>
                <SelectInput
                  label="Quantity Type"
                  tooltip={qtyShadowed
                    ? 'Ignored at run time — sizing comes from the qty_calculator block (see banner above).'
                    : 'How position size is calculated for this entry'}
                  field="quantity_type"
                  value={selectedBlock.data.quantity_type}
                  onChange={handleInputChange}
                  hasError={getFieldValidation('quantity_type').hasError}
                  hasWarning={getFieldValidation('quantity_type').hasWarning}
                  options={(() => {
                    // S10i — F29 fix. Pre-S10i the dropdown listed
                    // Object.values(QuantityType) which omitted
                    // atr_risk / fixed_risk / pine_futures (those
                    // are engine-supported sizing keys but not in
                    // the legacy QuantityType enum). Pulled from
                    // engine via sizing.ts:307+. Listing all here
                    // so manual authors can pick any engine
                    // method.
                    const builtin = Object.values(QuantityType).map((type) => ({
                      value: type as string,
                      label: String(type).replace(/_/g, ' '),
                    }));
                    const extras = ['atr_risk', 'fixed_risk', 'pine_futures'].map((v) => ({
                      value: v,
                      label: v.replace(/_/g, ' '),
                    }));
                    // Dedup by value — QuantityType enum may
                    // already contain some of these.
                    const seen = new Set<string>();
                    return [...builtin, ...extras].filter((o) => {
                      if (seen.has(o.value)) return false;
                      seen.add(o.value);
                      return true;
                    });
                  })()}
                  defaultValue={QuantityType.FIXED}
                  disabled={qtyShadowed}
                />

                <NumberInput
                  label="Quantity Value"
                  tooltip={qtyValueShadowed
                    ? 'Ignored at run time — sizing comes from the qty_calculator block (see banner above).'
                    : 'Position size value (interpretation depends on quantity type)'}
                  field="quantity_value"
                  value={selectedBlock.data.quantity_value}
                  onChange={handleInputChange}
                  hasError={getFieldValidation('quantity_value').hasError}
                  hasWarning={getFieldValidation('quantity_value').hasWarning}
                  min={0.01}
                  step={0.01}
                  disabled={qtyValueShadowed}
                />

                {RISK_METHODS.has(qt) && (
                  <NumberInput
                    label="Risk Per Trade (%)"
                    tooltip={riskShadowed
                      ? 'Ignored at run time — sizing comes from the qty_calculator block (see banner above).'
                      : 'Percentage of account to risk per trade. Used by risk_based / atr_risk / pine_futures / fixed_risk / kelly_criterion / volatility_adjusted methods.'}
                    field="risk_per_trade"
                    value={selectedBlock.data.risk_per_trade}
                    onChange={handleInputChange}
                    hasError={getFieldValidation('risk_per_trade').hasError}
                    hasWarning={getFieldValidation('risk_per_trade').hasWarning}
                    min={0.1}
                    max={20}
                    step={0.1}
                    disabled={riskShadowed}
                  />
                )}

                {/*
                  S10i — F31 fix. Method-specific parameter fields.
                  Engine reads at sizing.ts:486-487, 654-655, 683-685.
                  Pre-S10i these fields existed in BlockData but
                  had zero UI surface.
                */}
                {qt === 'atr_risk' && (
                  <NumberInput
                    label="ATR Size Multiplier"
                    tooltip="Multiplier applied to ATR when computing the per-trade risk distance (atr_risk method)."
                    field="atr_size_multiplier"
                    value={(selectedBlock.data as Record<string, unknown>).atr_size_multiplier as number | undefined}
                    onChange={handleInputChange}
                    min={0.1}
                    step={0.1}
                  />
                )}
                {qt === 'fixed_risk' && (
                  <NumberInput
                    label="Fixed Risk ($)"
                    tooltip="Cash risk per trade (fixed_risk method). Engine reads sizing.ts:486-487."
                    field="fixed_risk_usd"
                    value={(selectedBlock.data as Record<string, unknown>).fixed_risk_usd as number | undefined}
                    onChange={handleInputChange}
                    min={1}
                    step={1}
                  />
                )}
                {qt === QuantityType.KELLY_CRITERION && (
                  <div className="grid grid-cols-2 gap-4">
                    <NumberInput
                      label="Kelly Lookback (trades)"
                      tooltip="How many recent trades to use for the Kelly fraction calculation."
                      field="kelly_lookback"
                      value={(selectedBlock.data as Record<string, unknown>).kelly_lookback as number | undefined}
                      onChange={handleInputChange}
                      min={10}
                      step={1}
                      parser={(v) => parseInt(v)}
                    />
                    <NumberInput
                      label="Kelly Fraction"
                      tooltip="Fraction of the full Kelly to bet (typical: 0.25 = quarter-Kelly for safety)."
                      field="kelly_fraction"
                      value={(selectedBlock.data as Record<string, unknown>).kelly_fraction as number | undefined}
                      onChange={handleInputChange}
                      min={0.05}
                      max={1}
                      step={0.05}
                    />
                  </div>
                )}
                {qt === QuantityType.VOLATILITY_ADJUSTED && (
                  <NumberInput
                    label="Volatility Lookback (bars)"
                    tooltip="Lookback for the volatility measurement that scales position size inversely."
                    field="volatility_lookback"
                    value={(selectedBlock.data as Record<string, unknown>).volatility_lookback as number | undefined}
                    onChange={handleInputChange}
                    min={5}
                    step={1}
                    parser={(v) => parseInt(v)}
                  />
                )}
                {/*
                  S10i — Position-size caps. Engine reads at
                  sizing.ts:423-426. Apply on top of method-switch
                  sizing as hard floor/ceiling on UNITS.
                */}
                <div className="grid grid-cols-3 gap-2">
                  <NumberInput
                    label="Min Size"
                    tooltip="Floor on the resolved qty. 0 = no floor."
                    field="min_position_size"
                    value={(selectedBlock.data as Record<string, unknown>).min_position_size as number | undefined}
                    onChange={handleInputChange}
                    min={0}
                    step={1}
                  />
                  <NumberInput
                    label="Max Size"
                    tooltip="Cap on the resolved qty. 0 / blank = no cap."
                    field="max_position_size"
                    value={(selectedBlock.data as Record<string, unknown>).max_position_size as number | undefined}
                    onChange={handleInputChange}
                    min={0}
                    step={1}
                  />
                  <NumberInput
                    label="Base Size"
                    tooltip="Baseline size used by Kelly + Volatility-Adjusted as the reference point."
                    field="base_position_size"
                    value={(selectedBlock.data as Record<string, unknown>).base_position_size as number | undefined}
                    onChange={handleInputChange}
                    min={0}
                    step={1}
                  />
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/*
        S10n (2026-06-03) — F32 fix. Bracket-order fields. Engine
        reads at sizing.ts:831-841, 961-968 when entryType is
        'limit' AND orderType === 'bracket'. Pre-S10n JSON-only;
        users authoring bracket orders had to drop into JSON.
        Gated to the limit/bracket combination so it doesn't
        clutter the form for market-order strategies.
      */}
      {selectedBlock.data.entryType === 'limit' && (
        <details className="rounded border border-gray-200 p-2">
          <summary className="cursor-pointer text-sm text-gray-700 font-medium">
            Bracket order (advanced)
          </summary>
          <div className="mt-2 space-y-2">
            <SelectInput
              label="Order Type"
              tooltip="When 'bracket', the engine places stop + take-profit orders attached to the limit fill. Otherwise places a plain limit."
              field="orderType"
              value={(selectedBlock.data as Record<string, unknown>).orderType as string | undefined}
              onChange={handleInputChange}
              options={[
                { value: '', label: '(plain limit)' },
                { value: 'bracket', label: 'Bracket (limit + attached SL + TP)' },
              ]}
            />
            {(selectedBlock.data as Record<string, unknown>).orderType === 'bracket' && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <NumberInput
                    label="Bracket SL (price)"
                    tooltip="Stop-loss price for the attached bracket. Absolute price in instrument units."
                    field="bracketStopLoss"
                    value={(selectedBlock.data as Record<string, unknown>).bracketStopLoss as number | undefined}
                    onChange={handleInputChange}
                    min={0}
                    step={0.01}
                  />
                  <SelectInput
                    label="Bracket SL Type"
                    tooltip="How the stop-loss price was computed (informational; the bracketStopLoss number is authoritative)."
                    field="bracketStopType"
                    value={(selectedBlock.data as Record<string, unknown>).bracketStopType as string | undefined}
                    onChange={handleInputChange}
                    options={[
                      { value: '', label: '(auto / unspecified)' },
                      { value: 'fixed_points', label: 'Fixed points' },
                      { value: 'fixed_percent', label: 'Fixed percent' },
                      { value: 'atr_based', label: 'ATR-based' },
                    ]}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <NumberInput
                    label="Bracket TP (price)"
                    tooltip="Take-profit price for the attached bracket."
                    field="bracketTakeProfit"
                    value={(selectedBlock.data as Record<string, unknown>).bracketTakeProfit as number | undefined}
                    onChange={handleInputChange}
                    min={0}
                    step={0.01}
                  />
                  <SelectInput
                    label="Bracket TP Type"
                    tooltip="How the TP price was computed."
                    field="bracketTPType"
                    value={(selectedBlock.data as Record<string, unknown>).bracketTPType as string | undefined}
                    onChange={handleInputChange}
                    options={[
                      { value: '', label: '(auto / unspecified)' },
                      { value: 'fixed_points', label: 'Fixed points' },
                      { value: 'fixed_percent', label: 'Fixed percent' },
                      { value: 'risk_reward', label: 'Risk/reward' },
                    ]}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <NumberInput
                    label="Trailing Amount"
                    tooltip="Optional: trail distance for the bracket SL once profit accrues."
                    field="trailingAmount"
                    value={(selectedBlock.data as Record<string, unknown>).trailingAmount as number | undefined}
                    onChange={handleInputChange}
                    min={0}
                    step={0.01}
                  />
                  <NumberInput
                    label="Trailing Activation"
                    tooltip="Price level at which the trailing stop on the bracket becomes active."
                    field="trailingActivation"
                    value={(selectedBlock.data as Record<string, unknown>).trailingActivation as number | undefined}
                    onChange={handleInputChange}
                    min={0}
                    step={0.01}
                  />
                </div>
              </>
            )}
          </div>
        </details>
      )}

      <CheckboxInput
        label="Send alert on entry"
        tooltip="Send notification when entry signal is triggered"
        field="alertOnEntry"
        value={selectedBlock.data.alertOnEntry}
        onChange={handleInputChange}
      />
      {selectedBlock.data.alertOnEntry && (
        <AlertDeliveryFields blockData={selectedBlock.data} alertType="entry" onChange={handleInputChange} />
      )}
    </div>
  );

  const renderExitFields = () => (
    <div className="space-y-4">
      <div className="bg-orange-50 p-3 rounded-md border border-orange-200">
        <h4 className="text-sm font-medium text-orange-800 mb-2">
          TSL Exit Terminal Block
        </h4>
        <p className="text-xs text-orange-700 leading-relaxed">
          This is a terminal block that closes positions. It operates
          independently from entry blocks and can only receive signals from
          Expression or Logic Gate blocks.
        </p>
      </div>

      <SelectInput
        label="Exit Type"
        tooltip="Type of order for market exit"
        field="exitType"
        value={selectedBlock.data.exitType}
        onChange={handleInputChange}
        hasError={getFieldValidation('exitType').hasError}
        hasWarning={getFieldValidation('exitType').hasWarning}
        options={[
          { value: 'market', label: 'Market Order' },
          { value: 'limit', label: 'Limit Order' },
          { value: 'stop', label: 'Stop Order' },
        ]}
        defaultValue="market"
      />

      <SelectInput
        label="Logic Operator"
        tooltip="How multiple exit signals are combined"
        field="logic"
        value={selectedBlock.data.logic}
        onChange={handleInputChange}
        options={[
          { value: 'AND', label: 'All conditions must be true (AND)' },
          { value: 'OR', label: 'Any condition can be true (OR)' },
        ]}
        defaultValue="AND"
      />

      <SelectInput
        label="Exit Mode"
        tooltip="How much of the position to close"
        field="exitMode"
        value={selectedBlock.data.exitMode}
        onChange={handleInputChange}
        options={[
          { value: ExitType.FULL, label: 'Close Full Position' },
          { value: ExitType.PARTIAL, label: 'Partial Close' },
        ]}
        defaultValue={ExitType.FULL}
      />

      {selectedBlock.data.exitMode === ExitType.PARTIAL && (
        <NumberInput
          label="Partial Quantity (%)"
          tooltip="Percentage of position to close on this exit signal"
          field="partialQuantity"
          value={selectedBlock.data.partialQuantity}
          onChange={handleInputChange}
          min={1}
          max={100}
        />
      )}

      {/*
        S10i (2026-06-03) — F34 fix. Position-targeting fields on
        EXIT. Engine: ClientSideBacktestEngine.ts:2246-2248 reads
        targetEntryBlockId, targetDirection, closeAll. Pre-S10i
        JSON-only — every Exit closed ALL positions regardless
        of which entry opened them.
      */}
      <details className="rounded border border-gray-200 p-2">
        <summary className="cursor-pointer text-sm text-gray-700 font-medium">Position targeting (advanced)</summary>
        <div className="mt-2 space-y-2">
          {(() => {
            const entryBlocks = blocks.filter(b => b.type === BlockType.ENTRY);
            return (
              <SelectInput
                label="Target Entry Block"
                tooltip="When set, this Exit closes ONLY positions opened by this specific entry. Leave blank to close any. Useful for pyramiding."
                field="targetEntryBlockId"
                value={(selectedBlock.data as Record<string, unknown>).targetEntryBlockId as string | undefined}
                onChange={handleInputChange}
                options={[
                  { value: '', label: '(any entry)' },
                  ...entryBlocks.map(b => ({
                    value: b.id,
                    label: `${b.data.name || 'Entry'} (${(b.data as Record<string, unknown>).direction || 'long'})`,
                  })),
                ]}
              />
            );
          })()}
          <SelectInput
            label="Target Direction"
            tooltip="When set, this Exit closes ONLY positions of this direction."
            field="targetDirection"
            value={(selectedBlock.data as Record<string, unknown>).targetDirection as string | undefined}
            onChange={handleInputChange}
            options={[
              { value: '', label: '(any direction)' },
              { value: 'long', label: 'Long only' },
              { value: 'short', label: 'Short only' },
            ]}
          />
          <CheckboxInput
            label="Close all positions"
            tooltip="When true, closes ALL positions when this Exit fires (overrides target filters above)."
            field="closeAll"
            value={(selectedBlock.data as Record<string, unknown>).closeAll as boolean | undefined}
            onChange={handleInputChange}
          />
        </div>
      </details>

      {/*
        S10i — F36 fix. Order expiration for limit exits. (exitType is
        'market' | 'limit'; there is no 'stop' exit producer/option.)
      */}
      {selectedBlock.data.exitType === 'limit' && (
        <div className="grid grid-cols-2 gap-4">
          <NumberInput
            label="Expiration Time"
            tooltip="Cancel the limit/stop exit if not filled within this duration."
            field="expirationTime"
            value={(selectedBlock.data as Record<string, unknown>).expirationTime as number | undefined}
            onChange={handleInputChange}
            min={0}
            step={1}
            parser={(v) => parseInt(v)}
          />
          <SelectInput
            label="Expiration Unit"
            tooltip="Unit for expirationTime."
            field="expirationUnit"
            value={(selectedBlock.data as Record<string, unknown>).expirationUnit as string | undefined}
            onChange={handleInputChange}
            options={[
              { value: 'seconds', label: 'Seconds' },
              { value: 'minutes', label: 'Minutes' },
              { value: 'hours', label: 'Hours' },
              { value: 'days', label: 'Days' },
              { value: 'bars', label: 'Bars' },
            ]}
            defaultValue="bars"
          />
        </div>
      )}

      <CheckboxInput
        label="Send alert on exit"
        tooltip="Send notification when exit signal is triggered"
        field="alertOnExit"
        value={selectedBlock.data.alertOnExit}
        onChange={handleInputChange}
      />
      {selectedBlock.data.alertOnExit && (
        <AlertDeliveryFields blockData={selectedBlock.data} alertType="exit" onChange={handleInputChange} />
      )}
    </div>
  );

  // Stop Loss and Take Profit fields (enhanced for TSL)
  const renderStopLossFields = () => (
    <div className="space-y-4">
      <div className="bg-red-50 p-3 rounded-md border border-red-200">
        <h4 className="text-sm font-medium text-red-800 mb-2">
          TSL Stop Loss Terminal Block
        </h4>
        <p className="text-xs text-red-700 leading-relaxed">
          This is a terminal block for stop loss protection. It operates
          independently and can only receive signals from Expression or Logic
          Gate blocks.
        </p>
      </div>

      <SelectInput
        label="Stop Loss Type"
        tooltip="Type of stop loss calculation"
        field="stopType"
        value={selectedBlock.data.stopType}
        onChange={handleInputChange}
        hasError={getFieldValidation('stopType').hasError}
        hasWarning={getFieldValidation('stopType').hasWarning}
        options={Object.values(StopType).map((type) => ({
          value: type,
          label: type.replace(/_/g, ' ').toUpperCase(),
        }))}
        defaultValue={StopType.FIXED_POINTS}
      />

      <NumberInput
        label="Stop Value"
        tooltip="Stop loss value (interpretation depends on stop type)"
        field="stopValue"
        value={selectedBlock.data.stopValue}
        onChange={handleInputChange}
        hasError={getFieldValidation('stopValue').hasError}
        hasWarning={getFieldValidation('stopValue').hasWarning}
        min={0}
        step={0.1}
      />

      {/*
        S10g (2026-06-03) — F37-F42 fix. Pre-S10g most stop-type-
        specific parameters were JSON-only despite being listed in
        the dropdown. Each gated subsection now covers its engine-
        consumed fields. Trailing-step + ATR multiplier + time-
        duration paths were already correct; the new gates below
        fill in trailing_limit / volatility_adjusted / breakeven /
        swept-extreme.
      */}
      {(selectedBlock.data.stopType === StopType.TRAILING ||
        selectedBlock.data.stopType === StopType.TRAILING_LIMIT) && (
        <div className="space-y-4">
          {/* T-1m — trailingType selects how the trail distance is derived:
              fixed points (legacy), ATR(14) × multiplier (volatility-
              adaptive — recommended on 1m/5m), or % of the trailing
              extreme. The engine uses the same distance for the initial
              stop at entry. */}
          <SelectInput
            label="Trailing Distance Mode"
            tooltip="points = fixed price distance (trailingStep) · atr = ATR(14) × multiplier, adapts to volatility · percentage = % of the trailing extreme price"
            field="trailingType"
            value={(selectedBlock.data as Record<string, unknown>).trailingType as string | undefined}
            onChange={handleInputChange}
            options={[
              { value: 'points', label: 'FIXED POINTS' },
              { value: 'atr', label: 'ATR-ADAPTIVE' },
              { value: 'percentage', label: 'PERCENTAGE' },
            ]}
            defaultValue="points"
          />
          <div className="grid grid-cols-2 gap-4">
            {((selectedBlock.data as Record<string, unknown>).trailingType ?? 'points') === 'points' && (
              <NumberInput
                label="Trailing Step"
                tooltip="Step size for trailing stop adjustment"
                field="trailingStep"
                value={selectedBlock.data.trailingStep || 1}
                onChange={handleInputChange}
                min={0.1}
                step={0.1}
              />
            )}
            {(selectedBlock.data as Record<string, unknown>).trailingType === 'atr' && (
              <NumberInput
                label="ATR Multiplier"
                tooltip="Trail distance = ATR(14) × this multiplier — adapts to volatility"
                field="atrMultiplier"
                value={selectedBlock.data.atrMultiplier || 2}
                onChange={handleInputChange}
                min={0.1}
                max={10}
                step={0.1}
              />
            )}
            {(selectedBlock.data as Record<string, unknown>).trailingType === 'percentage' && (
              <NumberInput
                label="Trailing Percent"
                tooltip="Trail distance = this % of the trailing extreme price"
                field="trailingPercent"
                value={(selectedBlock.data as Record<string, unknown>).trailingPercent as number | undefined}
                onChange={handleInputChange}
                min={0.05}
                step={0.05}
              />
            )}
            {/* F41 — activationPrice for trailing stops. */}
            <NumberInput
              label="Activation Price"
              tooltip="Price level at which the trailing stop becomes active. 0 = active immediately."
              field="activationPrice"
              value={(selectedBlock.data as Record<string, unknown>).activationPrice as number | undefined}
              onChange={handleInputChange}
              min={0}
              step={0.01}
            />
          </div>
        </div>
      )}

      {/* F37 — TRAILING_LIMIT also needs limitOffset (trailing-limit-
          specific: how far past the trailing reference the LIMIT
          order is placed). */}
      {selectedBlock.data.stopType === StopType.TRAILING_LIMIT && (
        <NumberInput
          label="Limit Offset"
          tooltip="Offset between the trailing reference and the limit price (in instrument units). Engine reads at sizing.ts trailing-limit branch."
          field="limitOffset"
          value={(selectedBlock.data as Record<string, unknown>).limitOffset as number | undefined}
          onChange={handleInputChange}
          min={0}
          step={0.01}
        />
      )}

      {selectedBlock.data.stopType === StopType.ATR_BASED && (
        <div className="grid grid-cols-2 gap-4">
          <NumberInput
            label="ATR Multiplier"
            tooltip="ATR multiplier for stop distance"
            field="atrMultiplier"
            value={selectedBlock.data.atrMultiplier || 2}
            onChange={handleInputChange}
            min={0.1}
            max={10}
            step={0.1}
          />
          {/* F40 — ATR period for the SL's own ATR (separate from
              the strategy_config ATR). Engine reads at sizing.ts. */}
          <NumberInput
            label="ATR Period"
            tooltip="Lookback period for the ATR used by this stop. Default 14."
            field="atrPeriod"
            value={(selectedBlock.data as Record<string, unknown>).atrPeriod as number | undefined}
            onChange={handleInputChange}
            min={1}
            step={1}
            parser={(v) => parseInt(v)}
          />
        </div>
      )}

      {/* F40 — ATR swept-extreme anchor (Pine v5 FTMO pattern). When
          anchor === 'swept_extreme', engine uses the highest/lowest
          point of the last `sweepLookback` bars as the stop anchor
          instead of the entry price. */}
      {selectedBlock.data.stopType === StopType.ATR_BASED && (
        <div className="grid grid-cols-2 gap-4">
          <SelectInput
            label="ATR Anchor"
            tooltip="Where to anchor the ATR-based stop. 'entry' = entry price (default). 'swept_extreme' = recent high/low (Pine v5 FTMO pattern). Engine reads at sizing.ts:879."
            field="anchor"
            value={(selectedBlock.data as Record<string, unknown>).anchor as string | undefined}
            onChange={handleInputChange}
            options={[
              { value: 'entry', label: 'Entry price (default)' },
              { value: 'swept_extreme', label: 'Recent extreme (swept_extreme)' },
            ]}
            defaultValue="entry"
          />
          {(selectedBlock.data as Record<string, unknown>).anchor === 'swept_extreme' && (
            <NumberInput
              label="Sweep Lookback (bars)"
              tooltip="How many bars back to find the high/low for the swept-extreme anchor."
              field="sweepLookback"
              value={(selectedBlock.data as Record<string, unknown>).sweepLookback as number | undefined}
              onChange={handleInputChange}
              min={1}
              step={1}
              parser={(v) => parseInt(v)}
            />
          )}
        </div>
      )}

      {/* F40 — tick-buffer-ticks + tickSize override for atr_based. */}
      {selectedBlock.data.stopType === StopType.ATR_BASED && (
        <div className="grid grid-cols-2 gap-4">
          <NumberInput
            label="Tick Buffer (ticks)"
            tooltip="Add this many ticks of buffer beyond the calculated stop level. Engine reads at sizing.ts:907."
            field="tickBufferTicks"
            value={(selectedBlock.data as Record<string, unknown>).tickBufferTicks as number | undefined}
            onChange={handleInputChange}
            min={0}
            step={1}
            parser={(v) => parseInt(v)}
          />
          <NumberInput
            label="Tick Size (override)"
            tooltip="Local tick-size override. Falls back to strategy_config.tickSize when 0."
            field="tickSize"
            value={(selectedBlock.data as Record<string, unknown>).tickSize as number | undefined}
            onChange={handleInputChange}
            min={0}
            step={0.0001}
          />
        </div>
      )}

      {/* F38 — VOLATILITY_ADJUSTED stop. */}
      {selectedBlock.data.stopType === StopType.VOLATILITY_ADJUSTED && (
        <div className="grid grid-cols-2 gap-4">
          <NumberInput
            label="Volatility Multiplier"
            tooltip="Multiplier applied to the rolling volatility for the stop distance."
            field="volatilityMultiplier"
            value={(selectedBlock.data as Record<string, unknown>).volatilityMultiplier as number | undefined}
            onChange={handleInputChange}
            min={0.1}
            step={0.1}
          />
          <NumberInput
            label="Volatility Period"
            tooltip="Lookback period for the volatility measurement."
            field="volatilityPeriod"
            value={(selectedBlock.data as Record<string, unknown>).volatilityPeriod as number | undefined}
            onChange={handleInputChange}
            min={1}
            step={1}
            parser={(v) => parseInt(v)}
          />
        </div>
      )}

      {/* F39 — BREAKEVEN stop + "move to breakeven" overlay. */}
      {selectedBlock.data.stopType === StopType.BREAKEVEN && (
        <div className="space-y-2">
          <NumberInput
            label="Breakeven Trigger"
            tooltip="Profit (in instrument units or R-multiples) at which the stop moves to breakeven."
            field="breakevenTrigger"
            value={(selectedBlock.data as Record<string, unknown>).breakevenTrigger as number | undefined}
            onChange={handleInputChange}
            min={0}
            step={0.01}
          />
          <NumberInput
            label="Breakeven Offset"
            tooltip="Offset above entry (long) / below entry (short) when stop hits breakeven. 0 = exact breakeven."
            field="breakevenOffset"
            value={(selectedBlock.data as Record<string, unknown>).breakevenOffset as number | undefined}
            onChange={handleInputChange}
            step={0.01}
          />
        </div>
      )}

      {/* F39 — "Move to breakeven" overlay (works ALONGSIDE any
          stop type, not gated to BREAKEVEN). */}
      <details className="rounded border border-gray-200 p-2">
        <summary className="cursor-pointer text-sm text-gray-700 font-medium">Move to breakeven (overlay)</summary>
        <div className="mt-2 space-y-2">
          <CheckboxInput
            label="Enable move-to-breakeven"
            tooltip="When profit crosses the trigger below, move the stop to breakeven. Works on top of any stop type."
            field="moveToBreakeven"
            value={(selectedBlock.data as Record<string, unknown>).moveToBreakeven as boolean | undefined}
            onChange={handleInputChange}
          />
          <NumberInput
            label="Move-to-BE Trigger"
            tooltip="Profit (instrument units or R-multiples) at which the move-to-breakeven fires."
            field="moveToBreakevenTrigger"
            value={(selectedBlock.data as Record<string, unknown>).moveToBreakevenTrigger as number | undefined}
            onChange={handleInputChange}
            min={0}
            step={0.01}
          />
          <NumberInput
            label="Move-to-BE Offset"
            tooltip="Offset above entry (long) / below entry (short) when the move-to-BE fires."
            field="moveToBreakevenOffset"
            value={(selectedBlock.data as Record<string, unknown>).moveToBreakevenOffset as number | undefined}
            onChange={handleInputChange}
            step={0.01}
          />
        </div>
      </details>

      {selectedBlock.data.stopType === StopType.TIME_BASED && (
        <div className="grid grid-cols-2 gap-4">
          <NumberInput
            label="Time Duration"
            tooltip="Duration before stop loss activation"
            field="timeDuration"
            value={selectedBlock.data.timeDuration || 60}
            onChange={handleInputChange}
            min={1}
            parser={(v) => parseInt(v)}
          />
          <SelectInput
            label="Time Unit"
            tooltip="Unit for time duration"
            field="timeUnit"
            value={selectedBlock.data.timeUnit || 'minutes'}
            onChange={handleInputChange}
            options={[
              { value: 'seconds', label: 'Seconds' },
              { value: 'minutes', label: 'Minutes' },
              { value: 'hours', label: 'Hours' },
              { value: 'days', label: 'Days' },
            ]}
          />
        </div>
      )}

      {/* F42 — exitMode + partialQuantity (already on EXIT block,
          missing on STOP_LOSS). Engine: audit doc line 271. */}
      <div className="grid grid-cols-2 gap-4">
        <SelectInput
          label="Exit Mode"
          tooltip="full = close entire position; partial = close a percentage."
          field="exitMode"
          value={selectedBlock.data.exitMode}
          onChange={handleInputChange}
          options={Object.values(ExitType).map((t) => ({
            value: t,
            label: t.replace(/_/g, ' '),
          }))}
          defaultValue={ExitType.FULL}
        />
        {selectedBlock.data.exitMode === ExitType.PARTIAL && (
          <NumberInput
            label="Partial Quantity (%)"
            tooltip="Percentage of the open position to close when this SL fires."
            field="partialQuantity"
            value={selectedBlock.data.partialQuantity}
            onChange={handleInputChange}
            min={1}
            max={100}
            step={1}
            parser={(v) => parseInt(v)}
          />
        )}
      </div>

      <CheckboxInput
        label="Send alert on stop loss"
        tooltip="Send notification when stop loss is triggered"
        field="alertOnStopLoss"
        value={selectedBlock.data.alertOnStopLoss}
        onChange={handleInputChange}
      />
      {selectedBlock.data.alertOnStopLoss && (
        <AlertDeliveryFields blockData={selectedBlock.data} alertType="stop_loss" onChange={handleInputChange} />
      )}
    </div>
  );

  const renderTakeProfitFields = () => (
    <div className="space-y-4">
      <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
        <h4 className="text-sm font-medium text-blue-800 mb-2">
          TSL Take Profit Terminal Block
        </h4>
        <p className="text-xs text-blue-700 leading-relaxed">
          This is a terminal block for profit targets. It operates independently
          and can only receive signals from Expression or Logic Gate blocks.
        </p>
      </div>

      <SelectInput
        label="Take Profit Type"
        tooltip="Type of take profit calculation"
        field="tpType"
        value={selectedBlock.data.tpType}
        onChange={handleInputChange}
        hasError={getFieldValidation('tpType').hasError}
        hasWarning={getFieldValidation('tpType').hasWarning}
        options={Object.values(TakeProfitType).map((type) => ({
          value: type,
          label: type.replace(/_/g, ' ').toUpperCase(),
        }))}
        defaultValue={TakeProfitType.FIXED_POINTS}
      />

      <NumberInput
        label="Take Profit Value"
        tooltip="Take profit value (interpretation depends on TP type)"
        field="tpValue"
        value={selectedBlock.data.tpValue}
        onChange={handleInputChange}
        hasError={getFieldValidation('tpValue').hasError}
        hasWarning={getFieldValidation('tpValue').hasWarning}
        min={0}
        step={0.1}
      />

      {selectedBlock.data.tpType === TakeProfitType.RISK_REWARD && (
        <>
          <NumberInput
            label="Risk/Reward Ratio"
            tooltip="Risk to reward ratio (e.g., 1:2 = 2)"
            field="riskRewardRatio"
            value={selectedBlock.data.riskRewardRatio || 2}
            onChange={handleInputChange}
            min={0.1}
            max={10}
            step={0.1}
          />
          {/*
            S10h (2026-06-03) — F44 fix. linkedStopLossBlockId
            picks WHICH STOP_LOSS block's distance to use for the
            risk_reward math. Pre-S10h this was JSON-only;
            strategies with multiple SL blocks silently used the
            first one found. Engine: graphCompiler.ts:982 and
            sizing.ts:990. Dropdown populated from sibling
            STOP_LOSS blocks; "(auto: first found)" when blank.
          */}
          {(() => {
            const slBlocks = blocks.filter(b => b.type === BlockType.STOP_LOSS);
            if (slBlocks.length === 0) {
              return (
                <div className="p-2 bg-yellow-50 border border-yellow-200 rounded-md text-xs text-yellow-700">
                  ⚠ Risk/reward TP needs at least one STOP_LOSS block in the strategy.
                  Add one so the engine can compute the risk distance.
                </div>
              );
            }
            return (
              <SelectInput
                label="Linked Stop Loss Block"
                tooltip="Which STOP_LOSS block's distance to use for risk-reward math. Leave blank to auto-pick the first one. Engine: sizing.ts:990."
                field="linkedStopLossBlockId"
                value={(selectedBlock.data as Record<string, unknown>).linkedStopLossBlockId as string | undefined}
                onChange={handleInputChange}
                options={[
                  { value: '', label: '(auto: first found)' },
                  ...slBlocks.map(b => ({
                    value: b.id,
                    label: `${b.data.name || 'Stop Loss'} (${(b.data as Record<string, unknown>).stopType || 'fixed_points'})`,
                  })),
                ]}
              />
            );
          })()}
        </>
      )}

      {selectedBlock.data.tpType === TakeProfitType.ATR_BASED && (
        <NumberInput
          label="ATR Multiplier"
          tooltip="ATR multiplier for profit target"
          field="atrMultiplier"
          value={selectedBlock.data.atrMultiplier || 3}
          onChange={handleInputChange}
          min={0.1}
          max={20}
          step={0.1}
        />
      )}

      {/*
        S10h — F43 fix. TRAILING take-profit needs trailingOffset
        + activationLevel inputs. Pre-S10h listed in the dropdown
        but the trailing parameters were JSON-only.
      */}
      {selectedBlock.data.tpType === TakeProfitType.TRAILING && (
        <div className="grid grid-cols-2 gap-4">
          <NumberInput
            label="Trailing Offset"
            tooltip="How far the TP trails behind the favorable price (in instrument units)."
            field="trailingOffset"
            value={(selectedBlock.data as Record<string, unknown>).trailingOffset as number | undefined}
            onChange={handleInputChange}
            min={0}
            step={0.01}
          />
          <NumberInput
            label="Activation Level"
            tooltip="Price level at which trailing begins. 0 = active immediately."
            field="activationLevel"
            value={(selectedBlock.data as Record<string, unknown>).activationLevel as number | undefined}
            onChange={handleInputChange}
            min={0}
            step={0.01}
          />
        </div>
      )}

      {/*
        S10h — F45 fix. Position-targeting fields for multi-entry
        strategies. Engine: ClientSideBacktestEngine.ts:2246-2248
        reads targetEntryBlockId, targetDirection, closeAll on
        Exit / SL / TP terminals. Pre-S10h JSON-only.
      */}
      <details className="rounded border border-gray-200 p-2">
        <summary className="cursor-pointer text-sm text-gray-700 font-medium">Position targeting (advanced)</summary>
        <div className="mt-2 space-y-2">
          {(() => {
            const entryBlocks = blocks.filter(b => b.type === BlockType.ENTRY);
            return (
              <SelectInput
                label="Target Entry Block"
                tooltip="When set, this TP closes ONLY positions opened by this specific entry. Leave blank to close any position. Useful for pyramiding / multi-entry strategies."
                field="targetEntryBlockId"
                value={(selectedBlock.data as Record<string, unknown>).targetEntryBlockId as string | undefined}
                onChange={handleInputChange}
                options={[
                  { value: '', label: '(any entry)' },
                  ...entryBlocks.map(b => ({
                    value: b.id,
                    label: `${b.data.name || 'Entry'} (${(b.data as Record<string, unknown>).direction || 'long'})`,
                  })),
                ]}
              />
            );
          })()}
          <SelectInput
            label="Target Direction"
            tooltip="When set, this TP closes ONLY positions of this direction."
            field="targetDirection"
            value={(selectedBlock.data as Record<string, unknown>).targetDirection as string | undefined}
            onChange={handleInputChange}
            options={[
              { value: '', label: '(any direction)' },
              { value: 'long', label: 'Long only' },
              { value: 'short', label: 'Short only' },
            ]}
          />
          <CheckboxInput
            label="Close all positions"
            tooltip="When true, closes ALL positions (including ones opened by other entries) when this TP fires."
            field="closeAll"
            value={(selectedBlock.data as Record<string, unknown>).closeAll as boolean | undefined}
            onChange={handleInputChange}
          />
        </div>
      </details>

      {/* S4c (2026-06-02): Scaled Targets ladder editor removed.
          The `targets[]` array editor used to render when
          tpType === TakeProfitType.SCALED, writing into block.data.targets.
          The engine never iterated this field — `detectUnimplementedExits`
          in src/engine/strategyGuards.ts already surfaces `scaled` tpType
          as unimplemented, so any strategy that picks it gets a guard
          warning regardless of the editor's existence. A future S5 slice
          that implements scaled exits will re-introduce both this UI
          editor and the underlying `targets[]` schema field together.
          See docs/BLOCK_SETTINGS_AUDIT.md S4c section. */}

      <CheckboxInput
        label="Send alert on take profit"
        tooltip="Send notification when take profit is triggered"
        field="alertOnTakeProfit"
        value={selectedBlock.data.alertOnTakeProfit}
        onChange={handleInputChange}
      />
      {selectedBlock.data.alertOnTakeProfit && (
        <AlertDeliveryFields blockData={selectedBlock.data} alertType="take_profit" onChange={handleInputChange} />
      )}
    </div>
  );

  const renderCustomBlockFields = () => (
    <>
      {/*
        S8e (2026-06-03) — The Custom Block code editor is a full-
        screen modal that overlays the BlockEditor. When OPEN, it
        portals to body root (escapes stacking contexts). When the
        user closes the modal via its X, we just hide the modal —
        keeping the underlying block selected so the right-side
        panel (block name / description / Dependencies / Block ID
        footer) remains interactive. The placeholder below lets
        the user re-open the modal without re-selecting the block
        from the canvas.
      */}
      {isCodeEditorOpen && (
        <Suspense fallback={<CustomBlockEditorLoading />}>
          <CustomBlockEditor
            block={selectedBlock}
            onChange={handleInputChange}
            onClose={() => setIsCodeEditorOpen(false)}
          />
        </Suspense>
      )}
      {!isCodeEditorOpen && (
        <div className="p-4 border border-dashed border-gray-300 rounded-md bg-gray-50 text-sm text-gray-600">
          <div className="mb-2">
            Custom block code editor is closed. The block stays selected
            so you can use the Dependencies panel below.
          </div>
          <button
            type="button"
            onClick={() => setIsCodeEditorOpen(true)}
            className="px-3 py-1.5 bg-violet-600 text-white rounded hover:bg-violet-700 text-sm"
          >
            Open Code Editor
          </button>
        </div>
      )}
    </>
  );

  const renderBlockSpecificContent = () => {
    switch (selectedBlock.type) {
      case BlockType.STRATEGY_CONFIG:
        return renderStrategyConfigFields();
      case BlockType.PRICE_DATA:
        return renderPriceDataFields();
      // Technical indicators
      case BlockType.MOVING_AVERAGE:
        return renderMovingAverageFields();
      case BlockType.MOMENTUM_OSCILLATOR:
        return renderMomentumOscillatorFields();
      case BlockType.VOLATILITY:
        return renderVolatilityFields();
      case BlockType.VOLUME:
        return renderVolumeFields();
      case BlockType.TREND:
        return renderTrendFields();
      case BlockType.CUSTOM:
        return renderCustomBlockFields();
      // Logic blocks
      case BlockType.EXPRESSION:
        return renderExpressionFields();
      case BlockType.LOGIC_GATE:
        return renderLogicGateFields();
      // TSL Pattern Recognition blocks (3 separate types)
      case BlockType.CHART_PATTERNS:
        return renderChartPatternsFields();
      case BlockType.CANDLESTICK_PATTERNS:
        return renderCandlestickPatternsFields();
      // M-A — duplicate-case removed. `case BlockType.CUSTOM:` was shadowed
      // by the earlier one at line ~3116 (renderCustomBlockFields). Pattern-
      // style custom rendering is unreachable today; merged into the editor's
      // sub-type branch in renderCustomBlockFields. Same TECH DEBT as the
      // factory switch above: split BlockType.CUSTOM into two enum values.
      // Market filters
      case BlockType.TIME_FILTER:
        return renderTimeFilterFields();
      // Terminal blocks
      case BlockType.ENTRY:
        return renderEntryFields();
      case BlockType.EXIT:
        return renderExitFields();
      case BlockType.STOP_LOSS:
        return renderStopLossFields();
      case BlockType.TAKE_PROFIT:
        return renderTakeProfitFields();
      default:
        return (
          <div className="text-center py-8 text-gray-500">
            <Settings className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>
              Configuration options for this TSL block type are not yet
              implemented.
            </p>
            <p className="text-xs mt-2">Block Type: {selectedBlock.type}</p>
          </div>
        );
    }
  };

  return (
    <div
      className="bg-white border-l border-gray-200 w-80"
      style={{
        maxHeight: '79vh',         // Or use '600px' for a fixed pixel value
        overflowY: 'auto',         // Always allow vertical scrolling
      }}
    >
      <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center space-x-2">
          <h3 className="text-lg font-medium text-gray-800">
            TSL Block Editor
          </h3>
          {isDirty && (
            <div
              className="w-2 h-2 bg-orange-400 rounded-full"
              title="Unsaved changes"
            />
          )}
        </div>
        <div className="flex items-center space-x-1">
          {onDuplicate && (
            <button
              onClick={handleDuplicate}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
              title="Duplicate Block"
            >
              <Copy className="w-4 h-4" />
            </button>
          )}
          {sizingDiffersFromOriginal && (
            <button
              onClick={handleRestoreSizing}
              className="inline-flex items-center gap-1 px-1.5 py-1 text-[11px] font-medium text-indigo-600 border border-indigo-200 rounded hover:bg-indigo-50"
              title="Restore this block's sizing to the strategy's original (as-loaded) values."
            >
              <RotateCcw className="w-3.5 h-3.5" /> Restore sizing
            </button>
          )}
          <button
            onClick={handleReset}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
            title="Reset to Defaults"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
          {selectedBlock.data.locked ? (
            <span title="Block is locked">
              <Lock className="w-4 h-4 text-gray-400" />
            </span>
          ) : (
            <span title="Block is unlocked">
              <Unlock className="w-4 h-4 text-gray-400" />
            </span>
          )}
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="p-3 space-y-4">
        <div className="space-y-3">
          {/*
            S8i (2026-06-03) — "Block role" badge. When the selected
            block is a CUSTOM block, surface its semantic role
            (Indicator / Quantity Calculator / Stop Loss Calculator
            / etc.) right above the Block Name input. Pre-S8i this
            information was buried inside the modal's Block Type
            dropdown — and worse, that dropdown SILENTLY MISLABELED
            qty/sl/tp/risk_rule blocks as "Indicator" because their
            values weren't in the option list (S8i fixed that too).
            Now the badge is visible without opening the modal, so
            the user can see at a glance whether they're looking
            at a per-bar indicator or a per-trade calc.
            Color-coded: blue for per-bar, orange for per-trade
            (matches the banner color scheme below).
          */}
          {selectedBlock.type === BlockType.CUSTOM && (() => {
            const subtype = String(
              (selectedBlock.data as Record<string, unknown> | undefined)?.blockType ?? ''
            );
            if (!subtype) return null;
            const isPerTrade = isPerTradeSubtype(subtype);
            const isPerBar = isPerBarSubtype(subtype);
            if (!isPerBar && !isPerTrade) return null;
            return (
              <div className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                isPerTrade
                  ? 'bg-orange-100 text-orange-800 border border-orange-200'
                  : 'bg-blue-100 text-blue-800 border border-blue-200'
              }`}
              title={isPerTrade
                ? 'This block is invoked at trade lifecycle points (per-trade contract). See the contract banner below for the function signature.'
                : 'This block runs once per bar (per-bar contract). See the dual-channel banner below for the return shape.'}
              >
                Role: {subtypeLabel(subtype)}
              </div>
            );
          })()}

          <TextInput
            label="Block Name"
            tooltip="Name for this block"
            field="name"
            value={selectedBlock.data.name}
            onChange={handleInputChange}
            placeholder="Enter block name..."
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={selectedBlock.data.description || ''}
              onChange={(e) =>
                handleInputChange('description', e.target.value)
              }
              className={INPUT_CLASS}
              rows={2}
              placeholder="Enter description..."
            />
          </div>
        </div>

        {(blockErrors.length > 0 || blockWarnings.length > 0) && (
          <div className="space-y-2">
            {blockErrors.map((error, index) => (
              <div
                key={index}
                className="p-2 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm flex items-start"
              >
                <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium">{error.message}</div>
                  {error.suggestion && (
                    <div className="text-xs mt-1">{error.suggestion}</div>
                  )}
                </div>
              </div>
            ))}

            {blockWarnings.map((warning, index) => (
              <div
                key={index}
                className="p-2 bg-yellow-50 border border-yellow-200 rounded-md text-yellow-700 text-sm flex items-start"
              >
                <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium">{warning.message}</div>
                  {warning.suggestion && (
                    <div className="text-xs mt-1">{warning.suggestion}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {renderBlockSpecificContent()}
      </div>

      {/*
        S8d (2026-06-03) — Dependencies panel.
        Surfaces edge-based graph dependencies (connections.inputs /
        outputs) AND id-pointer runtime dependencies (data.*BlockId
        fields like qtyCalculatorBlockId / slCalculatorBlockId /
        tpCalculatorBlockId / leftInputBlockId / rightInputBlockId)
        for the currently-selected block. Rows are clickable when
        `onSelectBlockId` is provided AND the referenced block
        resolves; stale id pointers render as muted, non-clickable
        rows. UI-only — the data model is pure
        (./blockDependencies.ts) and tested independently.
      */}
      <DependenciesPanel
        selected={selectedBlock}
        blocks={blocks}
        onSelectBlockId={onSelectBlockId}
      />

      <div className="border-t border-gray-200 p-3 bg-gray-50">
        <div className="flex justify-between items-center">
          <div className="text-xs text-gray-500">
            Block ID: ...{selectedBlock.id.slice(-8)}
          </div>
          <div className="flex space-x-2">
            {onDelete && (
              <button
                onClick={handleDelete}
                className="px-3 py-1 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
              >
                Delete
              </button>
            )}
            <button
              onClick={onClose}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center space-x-1"
            >
              <Save className="w-3 h-3" />
              <span>Save</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── S8d (2026-06-03) — Dependencies panel ──────────────────────────────
//
// Renders the four sections defined in ./blockDependencies.ts:
//   Graph inputs / Graph outputs   (edge-based, connections.*)
//   Runtime refs                    (data.*BlockId pointers)
//   Referenced by                   (reverse scan — who points at me?)
//
// All rendering logic lives here; the data model is the pure helper
// `computeBlockDependencies`. When `onSelectBlockId` is provided AND
// the row's `resolved === true`, the row is rendered as a clickable
// button that calls the callback with the referenced block id.
// Unresolved (stale) rows render as muted text — the user sees that
// the id exists in the strategy data but the referenced block is
// gone, which is a real lint signal.

interface DependenciesPanelProps {
  selected: Block;
  blocks: Block[];
  onSelectBlockId?: (id: string) => void;
}

const DependenciesPanel: React.FC<DependenciesPanelProps> = ({
  selected,
  blocks,
  onSelectBlockId,
}) => {
  const deps = computeBlockDependencies(selected, blocks);

  // Hide the panel entirely when there is nothing to show. A pure-
  // indicator block with no edges + no refs has all four sections
  // empty; a panel header in that case would just be noise.
  const total =
    deps.graphInputs.length +
    deps.graphOutputs.length +
    deps.runtimeRefs.length +
    deps.referencedBy.length;
  if (total === 0) return null;

  // S8h (2026-06-03) — Dual-channel info banner. Renders only when
  // the selected block is a PER-BAR CUSTOM block (indicator /
  // pattern / analysis / filter / signal_generator). Per-trade
  // custom blocks (qty_calculator / sl_calculator / tp_calculator /
  // risk_rule) use a different invocation path and don't emit a
  // BlockOutput at all, so the dual-channel contract doesn't
  // apply to them.
  //
  // The banner addresses the most common question users hit when
  // wiring a custom indicator into a downstream block: "what does
  // the consumer actually see — a number or a boolean?". Answer:
  // depends on the consumer's block type, surfaced here + on
  // every Graph-output row's "reads as ..." suffix.
  const customSubType = selected.type === BlockType.CUSTOM
    ? (selected.data as Record<string, unknown> | undefined)?.blockType
    : undefined;
  const customSubTypeStr = typeof customSubType === 'string' ? customSubType : '';
  const isPerBarCustom = isPerBarSubtype(customSubTypeStr);
  // S8i (2026-06-03) — Per-trade subtype detection. When the block
  // is qty/sl/tp_calculator or risk_rule, render a different banner
  // explaining the per-trade contract (not the dual-channel one).
  const isPerTradeCustom = isPerTradeSubtype(customSubTypeStr);
  const perTradeCRaw = isPerTradeCustom ? perTradeContract(customSubTypeStr) : null;
  // S10q (2026-06-03) — Reflect the entryFunctionName override in
  // the per-trade contract badge so renames flow through to the
  // right-panel sidebar text. Reads selected.data.entryFunctionName;
  // applyFnNameOverride is a no-op when missing / empty / invalid.
  const perTradeOverride = selected.type === BlockType.CUSTOM
    ? (selected.data as Record<string, unknown> | undefined)?.entryFunctionName
    : undefined;
  const perTradeOverrideStr = typeof perTradeOverride === 'string' ? perTradeOverride : undefined;
  const perTradeC = perTradeCRaw
    ? applyFnNameOverride(perTradeCRaw, perTradeOverrideStr)
    : null;
  const perTradeRenamed = perTradeC != null && perTradeCRaw != null && perTradeC.fnName !== perTradeCRaw.fnName;

  return (
    <div className="border-t border-gray-200 p-3 bg-white">
      <div className="text-xs font-semibold text-gray-700 mb-2">Dependencies</div>

      {isPerBarCustom && (
        <div className="mb-3 rounded border border-blue-200 bg-blue-50 p-2 text-xs text-blue-900">
          <div className="font-semibold mb-1">Dual-channel output</div>
          <div>
            This custom block emits TWO parallel per-bar tracks: a
            numeric <span className="font-mono">value</span> series AND a boolean
            <span className="font-mono"> signals</span> series. What each downstream consumer
            sees depends on its block type:
          </div>
          <ul className="mt-1 ml-4 list-disc">
            <li><span className="font-semibold">Expression</span> blocks read the <span className="font-mono">value</span> series (numeric comparison).</li>
            <li><span className="font-semibold">Logic gate</span> and <span className="font-semibold">Entry / Exit / Stop Loss / Take Profit</span> terminals read the <span className="font-mono">signals</span> series (boolean truth-per-bar).</li>
          </ul>
          <div className="mt-1">
            If your <span className="font-mono">calculate(...)</span> returns only{' '}
            <span className="font-mono">value</span>, the engine derives{' '}
            <span className="font-mono">signals</span> implicitly as{' '}
            <span className="font-mono">value &gt; 0</span> at each bar. Set{' '}
            <span className="font-mono">signals</span> explicitly to control the boolean channel.
          </div>
          <div className="mt-1">
            The <span className="font-semibold">Graph outputs</span> rows below name the channel each
            consumer will read.
          </div>
        </div>
      )}

      {/*
        S8i (2026-06-03) — Per-trade contract banner. Renders for
        qty_calculator / sl_calculator / tp_calculator / risk_rule
        blocks. Explicitly names the function the engine looks for,
        the signature, when it fires, and which pointer field the
        host block must set to activate dispatch. Closes the user's
        repeated question: "how does the entry block know this is
        for quantity?" — the answer is the pointer field, named
        here AND on the entry's S8f override banner. Crucially
        spells out that the canvas edge is decorative; runtime
        dispatch is by id, not by edge.
      */}
      {isPerTradeCustom && perTradeC && (
        <div className="mb-3 rounded border border-orange-200 bg-orange-50 p-2 text-xs text-orange-900">
          <div className="font-semibold mb-1">{subtypeLabel(customSubTypeStr)}</div>
          <div className="mt-1">
            <span className="font-mono">{perTradeC.signature}</span>
            {perTradeRenamed && perTradeCRaw && (
              <span className="ml-1 text-orange-700">
                (renamed from <span className="font-mono">{perTradeCRaw.fnName}</span>)
              </span>
            )}
          </div>
          <div className="mt-1">{perTradeC.summary}</div>
          <div className="mt-1">
            Your code must define{' '}
            <span className="font-mono">function {perTradeC.fnName}(...)</span> — the engine looks for that exact name.
            {perTradeC.pointerField && (
              <>
                {' '}<span className="font-semibold">To wire dispatch:</span> draw a canvas edge
                from this block to the <span className="font-semibold">{perTradeC.pointerHost}</span>.
                The edge auto-sets the{' '}
                <span className="font-mono">{perTradeC.pointerField}</span> pointer on the entry
                (S8j). Removing the edge clears the pointer. The engine reads the pointer at run
                time. See the <span className="font-semibold">Referenced by</span> section below to
                find which entries currently use this block.
              </>
            )}
          </div>
        </div>
      )}

      <DepSection
        label="Graph inputs"
        hint="Upstream blocks wired into this one via the canvas (connections.inputs). The channel suffix on each row names which BlockOutput track THIS block reads from upstream (Expression: values; LogicGate / terminals: signals; everything else: edge ignored)."
        rows={deps.graphInputs}
        onSelectBlockId={onSelectBlockId}
      />
      <DepSection
        label="Graph outputs"
        hint="Blocks this one feeds via the canvas (connections.outputs). The channel suffix on each row names which BlockOutput track that CONSUMER reads from us."
        rows={deps.graphOutputs}
        onSelectBlockId={onSelectBlockId}
      />
      <DepSection
        label="Runtime refs"
        hint="Id pointers on this block's data — NOT canvas edges. Engine resolves these by find() at run time (e.g. ENTRY.qtyCalculatorBlockId). The BlockOutput channel system does not apply — calc blocks are invoked per-trade with their own ctx surface."
        rows={deps.runtimeRefs}
        onSelectBlockId={onSelectBlockId}
      />
      <DepSection
        label="Referenced by"
        hint="Other blocks that point at this one via a Runtime ref field. Useful for sizing / SL / TP calculator blocks to see which entries use them."
        rows={deps.referencedBy}
        onSelectBlockId={onSelectBlockId}
      />
    </div>
  );
};

interface DepSectionProps {
  label: string;
  hint: string;
  rows: DependencyRef[];
  onSelectBlockId?: (id: string) => void;
}

const DepSection: React.FC<DepSectionProps> = ({ label, hint, rows, onSelectBlockId }) => {
  if (rows.length === 0) return null;
  return (
    <div className="mb-2 last:mb-0">
      <div
        className="text-[11px] uppercase tracking-wide text-gray-500"
        title={hint}
      >
        {label}
      </div>
      <ul className="mt-1 space-y-0.5">
        {rows.map((r, i) => (
          <DepRow
            key={`${r.refId}-${r.via}-${i}`}
            row={r}
            onSelectBlockId={onSelectBlockId}
          />
        ))}
      </ul>
    </div>
  );
};

interface DepRowProps {
  row: DependencyRef;
  onSelectBlockId?: (id: string) => void;
}

const DepRow: React.FC<DepRowProps> = ({ row, onSelectBlockId }) => {
  // S8f (2026-06-03) — formatDependencyRowFull appends the `via`
  // field name AND the override list (e.g.
  // "...001c · qtyCalculatorBlockId · overrides: quantity_type,
  // quantity_value, ..."). Plain edge rows (empty `via`, empty
  // `overrides`) collapse to the compact form, so the line stays
  // short when there's nothing extra to say. Keeping the helper
  // out of the component so the conversion-test bundle can pin
  // the format directly.
  const formatted = formatDependencyRowFull(row);

  const clickable = row.resolved && typeof onSelectBlockId === 'function';

  if (!clickable) {
    return (
      <li
        className={
          row.resolved
            ? 'text-xs text-gray-600 font-mono'
            : 'text-xs text-gray-400 italic font-mono'
        }
        title={row.resolved
          ? `Block id ${row.refId}`
          : `Stale reference: no block with id ${row.refId} exists in this strategy. Clean up the pointer field on the referencing block.`}
      >
        {formatted}
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        onClick={() => onSelectBlockId?.(row.refId)}
        className="text-xs text-blue-600 hover:text-blue-800 hover:underline font-mono text-left w-full truncate"
        title={`Jump to block ${row.refId}`}
      >
        {formatted}
      </button>
    </li>
  );
};

export default BlockEditor;
